import { useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  Check,
  ChevronDown,
  ChevronUp,
  Plus,
  Tag,
  Trash2,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@siliconx/shared/ui/Button';
import { SectionLabel } from '@siliconx/shared/ui/SectionLabel';
import { StatusPill, type PillTone } from '@siliconx/shared/ui/StatusPill';
import type {
  FinancialScenarioRow,
  FinancialScenarioCohortRow,
  MeResponse,
  ScenarioAction,
  ScenarioCohortSelector,
  ScenarioCompareResponse,
  ScenarioSensitivityResponse,
  FmvBand,
  ScenarioHorizonMonths,
  BookVsFmvAssetsResponse,
  Listing,
  CarbonTargetsResponse,
  ScenarioStalenessDto,
  FinancialScenarioApprovalSnapshot,
  FinancialScenarioApprovalSnapshotResponse,
} from '@siliconx/shared';
import { SENSITIVITY_ATTRIBUTES, DIRECTIONAL_BASELINE_LABEL } from '@siliconx/shared';
import { cn } from '@siliconx/shared/ui/utils';
import { LoadingShell, ErrorState } from '@siliconx/shared/ui/state';
import { QueryBoundary } from '@siliconx/shared/ui/QueryBoundary';
import { BackLink } from '@siliconx/shared/ui/BackLink';
import { SX_PALETTE } from '@siliconx/shared/ui/charts';
import { CoverageRing, coverageTier, TIER_ACCENT } from '@siliconx/shared/ui/TelemetryScoreCallout';
import { api, throwIfNotOk } from '../../../lib/api';
import { formatDate, formatTs, formatUsd } from '../../../lib/format';
import { InfoTooltip } from '../../../components/financials/InfoTooltip';
import { RefreshCandidateTable } from '../../../components/financials/RefreshCandidateTable';
import { ScenarioCompareChart } from '../../../components/financials/ScenarioCompareChart';
import { ScenarioTimeSankeys } from '../../../components/financials/ScenarioTimeSankeys';
import { ExportButtonGroup } from '../../../components/financials/ExportButtons';
import { CalculationBasisTrigger } from '../../../components/financials/CalculationBasisDrawer';
import { ScenarioCarbonForecast, SensitivityCarbonSwing } from '../../../components/financials/ScenarioCarbonForecast';
import { CarbonAdvisoryModule } from '../../../components/financials/carbon/CarbonAdvisoryModule';
import { CarbonSensitivityTornado } from '../../../components/financials/carbon/CarbonSensitivityTornado';
import {
  ScenarioBuyMoveStep,
  BuyCohortSummaryModule,
  toCarbonCohortInputs,
  isBuyDraftComplete,
  isMoveDraftComplete,
  type CarbonBuyDraft,
  type CarbonMoveDraft,
} from '../../../components/financials/ScenarioBuyMoveStep';
import { useScenarioAutoCompute } from '../../../components/financials/useScenarioAutoCompute';
import { qk } from '../../../lib/queryKeys';
import { SingleFlight } from '../../../lib/singleFlight';

/**
 * Unwrap a typed hc client Response into its success-body T, throwing a
 * uniform Error on any non-2xx branch (matches lib/api.ts's throwIfNotOk
 * narrowing rule — `await throwIfNotOk(res)` alone does not narrow the TS
 * union, see lib/api.ts's doc comment).
 */
async function unwrap<T>(res: { ok: boolean; status: number; json: () => Promise<unknown> }): Promise<T> {
  if (!res.ok) {
    await throwIfNotOk(res as Response);
    throw new Error(`HTTP ${res.status}`);
  }
  return (await res.json()) as T;
}

/**
 * DESIGN.md's One Voice Rule — "direction comes from the series palette,
 * never from emerald" on a static figure. `SX_PALETTE[3]` (emerald-500) and
 * `SX_PALETTE[5]` (red-500) are the same two series-scale steps the compare
 * chart itself already uses for positive/negative; this is a plain style
 * color (not a Tailwind class) because the series scale ships as hex, not
 * as tokens.
 */
function deltaSeriesColor(delta: number): string {
  return delta >= 0 ? SX_PALETTE[3] : SX_PALETTE[5];
}

/**
 * A signed dollar delta, colored from the series scale (never emerald) with
 * the sign glyph doing the direction work for colorblind readers — see the
 * One Voice Rule in apps/customer/DESIGN.md.
 */
function SignedUsd({ value, className }: { value: number; className?: string }) {
  return (
    <span className={cn('font-num', className)} style={{ color: deltaSeriesColor(value) }}>
      {value >= 0 ? '+' : '−'}
      {formatUsd(Math.abs(value))}
    </span>
  );
}

/**
 * Section head + trailing basis/info trigger, deduplicated from four
 * copy-pasted call sites (:2341/:2368/:2788/:2842 pre-restructure).
 */
function SectionHead({ children, trigger }: { children: ReactNode; trigger?: ReactNode }) {
  return (
    <SectionLabel className="mb-1 inline-flex items-center gap-1.5">
      {children}
      {trigger}
    </SectionLabel>
  );
}

/**
 * The ONE disclosure idiom this route uses — button + chevron, proven first
 * on the carbon-lens toggle. Reused (not reinvented) for the evidence band's
 * collapsed-by-default Sankeys and the caveats band's single carbon
 * disclosure, per the module's "no second collapsible idiom" instruction.
 */
function CollapsibleCard({
  title,
  subtitle,
  open,
  onToggle,
  children,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  open: boolean;
  onToggle: () => void;
  children: ReactNode;
}) {
  const panelId = useId();
  return (
    <div className="rounded-xl border border-border bg-card p-5">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        aria-controls={panelId}
        className="flex w-full items-center justify-between gap-2 text-left"
      >
        <span>
          <SectionLabel className="mb-0">{title}</SectionLabel>
          {subtitle && <p className="mt-0.5 text-xs text-muted-foreground">{subtitle}</p>}
        </span>
        {open ? (
          <ChevronUp className="size-4 shrink-0 text-muted-foreground" />
        ) : (
          <ChevronDown className="size-4 shrink-0 text-muted-foreground" />
        )}
      </button>
      {open && (
        <div id={panelId} className="mt-4 space-y-6 border-t border-border pt-4">
          {children}
        </div>
      )}
    </div>
  );
}

/**
 * "Name this template" — replaces `window.prompt`, a native OS modal that
 * had no place inside a finance instrument (blocks the whole tab, can't be
 * themed, and isn't reachable via the app's own dialog conventions). Same
 * overlay/panel shape as `CalculationBasisDrawer`'s drawer — centered here
 * instead of a slide-over since this is a single input, not a report.
 */
function NameTemplateDialog({
  defaultName,
  isPending,
  onCancel,
  onSubmit,
}: {
  defaultName: string;
  isPending: boolean;
  onCancel: () => void;
  onSubmit: (name: string) => void;
}) {
  const [name, setName] = useState(defaultName);
  const titleId = useId();
  return (
    <div
      className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4"
      onClick={onCancel}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="w-full max-w-sm rounded-xl border border-border bg-card p-5 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id={titleId} className="text-sm font-semibold text-foreground">
          Name this template
        </h2>
        <input
          autoFocus
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && name.trim()) onSubmit(name.trim());
            if (e.key === 'Escape') onCancel();
          }}
          className="mt-3 w-full rounded-md border border-border px-2.5 py-1.5 text-sm text-foreground"
          aria-label="Template name"
        />
        <div className="mt-4 flex justify-end gap-2">
          <Button size="sm" variant="secondary" onClick={onCancel}>
            Cancel
          </Button>
          <Button size="sm" disabled={!name.trim() || isPending} onClick={() => onSubmit(name.trim())}>
            {isPending ? 'Saving…' : 'Save template'}
          </Button>
        </div>
      </div>
    </div>
  );
}

/**
 * /financials/scenarios/$scenarioId — scenario builder wizard (SIL-547, W3
 * of resources/plans/wayfinder-ux-consistency-spec.md §4, map SIL-524).
 * `$scenarioId === 'new'` is the create sentinel — nothing is persisted
 * until the first Save, after which the URL is replaced with the
 * server-assigned id so refresh/back-forward and stage navigation stay
 * stable.
 *
 * THREE STAGES (was five flat steps): Define paths → Review assumptions →
 * Compare & decide. Reference: `jarriett/sil-530-overview-prototype`'s
 * structural mocks, variant B (stage/tab shape) + variant X (Compare &
 * decide) — rewritten properly here, not copied.
 *
 * Stage 1 — "Define paths" — contains ONLY what `financial_scenario_cohort`
 * actually persists: Lifecycle paths (was "Cohorts & actions") and
 * Replacements (was "Refresh"), as freely-navigable tabs (no autosave gate
 * between them — a tab switch is not a stage transition). `buy`/`move` do
 * NOT appear here: per `ScenarioBuyMoveStep.tsx`'s own header they are
 * request-scoped carbon-lens inputs — absent from `scenario_action`, "not
 * dollar actions in v1", riding only on `POST /compare` as `carbonCohorts`,
 * with no price field. They live on stage 3 as a carbon lens instead.
 *
 * Stage 2 — "Review assumptions" — one table whose rows are
 * `SENSITIVITY_ATTRIBUTES` (imported, not re-typed) plus a client-only
 * discount-rate row, in that exact order, so this table and the sensitivity
 * tornado at the foot of stage 3 are always the same rows in the same order
 * — see `AssumptionTable`.
 *
 * Stage 3 — "Compare & decide" — "Your scenario vs. holding", never a
 * "recommended path": no ranking or candidate generation exists, and a
 * scenario is one authored path vs the synthetic baseline, so there is
 * nothing to rank.
 *
 * HORIZON MOVED TO THE HEADER (was Economics, two stages from the cohort
 * timing it validates — `isStranded`'s docstring below is the trap this
 * dissolves). The stranded-cohort guard itself is UNCHANGED: it still
 * blocks autosave, and both the per-cohort warning (stage 1) and the
 * aggregate warning (header, next to the control that causes it) render.
 *
 * BASELINE IS SYNTHETIC — "Baseline · Hold all assets" renders at the top
 * of stage 1's Lifecycle paths list but is never stored;
 * `financial_scenario_cohort` keeps holding only the exceptions. No schema
 * change.
 */

type Stage = 1 | 2 | 3;
type Stage1Tab = 'lifecycle' | 'replacements' | 'purchases';

const STAGE_SEARCH = (
  search: Record<string, unknown>,
  // `tab` is typed optional here (though the runtime value below always
  // resolves a concrete default) purely so `navigate({ search: { stage } })`
  // can omit it outbound for stage 2/3 — `Stage1Tab` is inert outside stage
  // 1's own branch, and the share URL already omits it; internal nav now
  // agrees with the share URL instead of always writing `&tab=lifecycle`.
): { stage: Stage; tab?: Stage1Tab; classId?: string } => {
  const rawStage = Number(search.stage);
  const stage: Stage = rawStage === 2 || rawStage === 3 ? rawStage : 1;
  const tab: Stage1Tab =
    search.tab === 'replacements' ? 'replacements' : search.tab === 'purchases' ? 'purchases' : 'lifecycle';
  // SIL-566 (I2-B4) — cross-pillar deep link: asset detail -> "model this
  // class in a scenario" lands here with ?classId=<owned asset class>. Only
  // consulted for a brand-new draft (see ScenarioWizardPage) — an existing
  // scenario's cohorts always come from the server.
  const classId = typeof search.classId === 'string' && search.classId ? search.classId : undefined;
  return { stage, tab, classId };
};

export const Route = createFileRoute('/financials/scenarios/$scenarioId')({
  component: ScenarioWizardPage,
  validateSearch: STAGE_SEARCH,
});

// ---------------------------------------------------------------------------
// Local draft shapes
// ---------------------------------------------------------------------------

/**
 * An asset class the org OWNS assets in — `GET /reference/owned-asset-classes`,
 * NOT the full `GET /reference` taxonomy. Every class picker on this wizard
 * resolves to owned hardware (a cohort, a relocation, a listing candidate),
 * so offering the whole catalog just let the reader pick a class they own
 * nothing in and land on an empty list with no explanation.
 *
 * `assetCount === 0` means "kept visible because something already selected
 * it", never "owned zero and offered anyway" — see `ownedClassIncludeIds`.
 */
interface AssetClassRef {
  id: string;
  name: string;
  assetCount: number;
}

/** Epic 2 (lots-first-class) — minimal shapes for the cohort scope's
 * "By lot"/"By team" selects. Mirrors `LotOption`/`Team` in
 * apps/customer/src/routes/assets/index.tsx. */
interface ScenarioLotOption {
  id: string;
  name: string;
}
interface ScenarioTeamOption {
  id: string;
  name: string;
}

export interface CohortDraft {
  key: string; // stable client-side key (== saved id once persisted)
  id: string | null; // null until this cohort has been saved
  label: string;
  /** Epic 2 (lots-first-class) added 'lot'/'team' — SINGLE-select scope, one
   * of the four; comparing e.g. Lot A vs Lot B is two cohort ROWS, never a
   * multi-select within one cohort (see plans/lots-first-class.md Epic 2
   * "One lot per cohort"). */
  scope: 'all' | 'class' | 'lot' | 'team';
  assetClassId: string | null;
  /** Epic 2 — set only when `scope === 'lot'`. */
  lotId: string | null;
  /** Epic 2 — set only when `scope === 'team'`. */
  teamId: string | null;
  action: ScenarioAction;
  timingMonths: number;
  refreshSkuId: string | null;
  /** Old:new ratio for `refresh`; REUSED (SIL-571) as the purchase's unit
   * count for `buy` — the two actions never coexist on one cohort. */
  consolidationRatio: number | null;
  /** SIL-571 — required when `action = 'buy'`. */
  buySkuId: string | null;
  /** SIL-571 — server-computed street-band median; read-only here (never
   * sent on a write body — see `toCohortWriteBody`). Null when suppressed. */
  buyUnitPriceUsd: number | null;
  /** SIL-571 — true when the buy SKU's market depth is below the
   * k-anonymity floor. Read-only, same posture as `buyUnitPriceUsd`. */
  buyPriceSuppressed: boolean;
  /**
   * SIL-522 — a selector shape this form does not model (e.g. `assetIds`,
   * set only by `POST /scenarios` callers outside the wizard). `scope`/
   * `assetClassId` above can only express `class`/`all`, so re-deriving the
   * write-body selector from them alone silently rewrote any other selector
   * to `{all:true}` on the very first autosave after opening the scenario —
   * no edit required. Carrying the original selector verbatim and only
   * clearing it when the reader actually edits scope/class (see `update` in
   * `CohortsStep`) makes round-tripping the default instead of an opt-in.
   */
  unmodeledSelector: ScenarioCohortSelector | null;
}

/** True for the FOUR selector shapes this wizard's Scope/Asset class/Lot/Team
 * fields can express (Epic 2 added `lotId`/`teamId` to the previous
 * `assetClassId`/`all` pair) — everything else (`assetIds`, or any future
 * shape) must be preserved verbatim rather than reconstructed from
 * `scope`/`assetClassId`/`lotId`/`teamId`. */
export function isModeledSelector(selector: ScenarioCohortSelector): boolean {
  const keys = Object.keys(selector) as (keyof ScenarioCohortSelector)[];
  return (
    keys.length === 0 ||
    (keys.length === 1 &&
      (keys[0] === 'assetClassId' || keys[0] === 'all' || keys[0] === 'lotId' || keys[0] === 'teamId'))
  );
}

interface ScenarioDraft {
  name: string;
  horizonMonths: ScenarioHorizonMonths;
  fmvBand: FmvBand;
  kwhUsd: number;
  pue: number | null;
  itadRecoveryPct: number;
  cohorts: CohortDraft[];
}

/** `financial_scenario.kwh_usd`/`.itad_recovery_pct`'s own column defaults
 * (`db/schema/681_financial_scenario.sql`) — named here once so the
 * assumption table's "Platform default" vs "Set for this scenario" tier
 * check (`AssumptionTable`) and `emptyDraft` can never drift apart. */
const PLATFORM_DEFAULT_KWH_USD = 0.12;
const PLATFORM_DEFAULT_ITAD_RECOVERY_PCT = 34;

let cohortKeySeq = 0;
const nextCohortKey = () => `draft-${++cohortKeySeq}`;

function emptyDraft(): ScenarioDraft {
  return {
    name: 'Untitled scenario',
    horizonMonths: 36,
    fmvBand: 'med',
    kwhUsd: PLATFORM_DEFAULT_KWH_USD,
    pue: null,
    itadRecoveryPct: PLATFORM_DEFAULT_ITAD_RECOVERY_PCT,
    cohorts: [],
  };
}

function draftFromRow(row: FinancialScenarioRow): ScenarioDraft {
  return {
    name: row.name,
    horizonMonths: row.horizonMonths,
    fmvBand: row.fmvBand,
    kwhUsd: row.kwhUsd,
    pue: row.pue,
    itadRecoveryPct: row.itadRecoveryPct,
    cohorts: (row.cohorts ?? []).map(cohortDraftFromRow),
  };
}

export function cohortDraftFromRow(row: FinancialScenarioCohortRow): CohortDraft {
  return {
    key: row.id,
    id: row.id,
    label: row.label,
    scope: row.selector.assetClassId
      ? 'class'
      : row.selector.lotId
        ? 'lot'
        : row.selector.teamId
          ? 'team'
          : 'all',
    assetClassId: row.selector.assetClassId ?? null,
    lotId: row.selector.lotId ?? null,
    teamId: row.selector.teamId ?? null,
    action: row.action,
    timingMonths: row.timingMonths,
    refreshSkuId: row.refreshSkuId,
    consolidationRatio: row.consolidationRatio,
    buySkuId: row.buySkuId,
    buyUnitPriceUsd: row.buyUnitPriceUsd,
    buyPriceSuppressed: row.buyPriceSuppressed,
    unmodeledSelector: isModeledSelector(row.selector) ? null : row.selector,
  };
}

export function newCohortDraft(): CohortDraft {
  return {
    key: nextCohortKey(),
    id: null,
    label: 'New cohort',
    scope: 'all',
    assetClassId: null,
    lotId: null,
    teamId: null,
    action: 'hold',
    timingMonths: 0,
    refreshSkuId: null,
    consolidationRatio: null,
    buySkuId: null,
    buyUnitPriceUsd: null,
    buyPriceSuppressed: false,
    unmodeledSelector: null,
  };
}

export function toCohortWriteBody(c: CohortDraft) {
  return {
    label: c.label.trim() || 'Cohort',
    // Verbatim round-trip: an unmodeled selector (e.g. `assetIds`) survives
    // untouched unless the reader edited scope/class, which clears it (see
    // `update` in `CohortsStep`).
    selector:
      c.unmodeledSelector ??
      (c.scope === 'class' && c.assetClassId
        ? { assetClassId: c.assetClassId }
        : c.scope === 'lot' && c.lotId
          ? { lotId: c.lotId }
          : c.scope === 'team' && c.teamId
            ? { teamId: c.teamId }
            : { all: true }),
    action: c.action,
    timingMonths: c.timingMonths,
    refreshSkuId: c.action === 'refresh' ? c.refreshSkuId : null,
    // Reused for buy's unit count (see the field's docstring on CohortDraft).
    consolidationRatio: c.action === 'refresh' || c.action === 'buy' ? c.consolidationRatio : null,
    buySkuId: c.action === 'buy' ? c.buySkuId : null,
    // buyUnitPriceUsd/buyPriceSuppressed are deliberately absent — always
    // server-computed (see `financialScenarioCohortWriteBaseSchema`'s
    // docstring), never sent from the client.
  };
}

/**
 * A cohort is "stranded" when its action would never occur inside the horizon.
 * `hold` is exempt: scenarioEngine's hold branch never reads `timingMonths`, so
 * a hold cohort's timing is inert and warning about it would be noise (and read
 * as the tautology "the Hold would never happen, so it models as a hold").
 */
function isStranded(c: Pick<CohortDraft, 'action' | 'timingMonths'>, horizonMonths: number): boolean {
  return c.action !== 'hold' && c.timingMonths > horizonMonths;
}

// ---------------------------------------------------------------------------
// Shared data hooks — one query, several consumers that must never drift
// ---------------------------------------------------------------------------

/**
 * `GET /scenarios/:id/sensitivity` — shared verbatim by `AssumptionTable`
 * (stage 2), `SensitivityPanel` (stage 3's tornado) and stage 3's confidence
 * count, all under the SAME react-query key, so the table and the tornado
 * are provably reading the same rows in the same order rather than two
 * independently-fetched copies that could disagree.
 */
function useScenarioSensitivityQuery(scenarioId: string | null) {
  return useQuery<ScenarioSensitivityResponse>({
    queryKey: qk.scenarios.sensitivity(scenarioId),
    queryFn: async () =>
      unwrap<ScenarioSensitivityResponse>(
        await api.api.client.scenarios[':id'].sensitivity.$get({ param: { id: scenarioId! } }),
      ),
    enabled: !!scenarioId,
  });
}

/**
 * `GET /scenarios/:id/approval-snapshot` (migration 748, SIL — scenario
 * freeze-on-approval follow-up) — the latest immutable, versioned freeze of
 * this scenario's `ScenarioCompareResult` as it stood the moment it was
 * approved. `enabled` on `isApproved` only: the endpoint 200s a
 * `not_approved` body for any other status, so fetching it for a draft/
 * in-review scenario would just be wasted network — the Compare & decide
 * page only ever needs this once `status === 'approved'`.
 */
function useScenarioApprovalSnapshotQuery(scenarioId: string | null, isApproved: boolean) {
  return useQuery<FinancialScenarioApprovalSnapshotResponse>({
    queryKey: qk.scenarios.approvalSnapshot(scenarioId),
    queryFn: async () =>
      unwrap<FinancialScenarioApprovalSnapshotResponse>(
        await api.api.client.scenarios[':id']['approval-snapshot'].$get({ param: { id: scenarioId! } }),
      ),
    enabled: !!scenarioId && isApproved,
  });
}

/**
 * Resolves which figures the Compare & decide page should render, given the
 * scenario's live status and (if approved) the approval-snapshot response —
 * the ONE place this branch is made, so a caller can't independently reach a
 * different answer than the page did. `unavailable` (approved pre-748, no
 * frozen row) MUST NEVER fall back to `live` — that reintroduces the exact
 * "same approved scenario renders a different number on two opens" bug this
 * feature closes; see the DTO's own doc comment in dto/financials.ts.
 *
 * The one intentional escape hatch: a scenario whose cached row still reads
 * `approved` but whose snapshot fetch just came back `not_approved` (the
 * two queries are invalidated together on approve/unapprove, but nothing
 * guarantees which resolves first) is treated as `live` rather than thrown
 * away — the row query will itself settle to `draft` moments later, and
 * `live` is what a draft scenario should show anyway.
 */
export type ApprovedCompareView =
  | { kind: 'live' }
  | { kind: 'frozen'; snapshot: FinancialScenarioApprovalSnapshot }
  | { kind: 'unavailable' };

export function resolveApprovedCompareView(
  status: FinancialScenarioRow['status'] | undefined,
  snapshotResponse: FinancialScenarioApprovalSnapshotResponse | undefined,
): ApprovedCompareView {
  if (status !== 'approved') return { kind: 'live' };
  if (!snapshotResponse || snapshotResponse.status === 'not_approved') return { kind: 'live' };
  if (snapshotResponse.status === 'unavailable') return { kind: 'unavailable' };
  return { kind: 'frozen', snapshot: snapshotResponse.snapshot };
}

/**
 * The figures the Compare & decide page actually renders, given the view
 * `resolveApprovedCompareView` resolved. THE regression this pins: for
 * `unavailable`, this returns `null` regardless of what `liveCompareResult`
 * holds — a live compute that happened to run in the background (e.g. a
 * stale auto-compute from before `status` was known) must never leak
 * through as a stand-in for the missing snapshot.
 */
export function resolveEffectiveCompareResult(
  view: ApprovedCompareView,
  liveCompareResult: ScenarioCompareResponse | null,
): ScenarioCompareResponse | null {
  if (view.kind === 'frozen') return view.snapshot.compareResult;
  if (view.kind === 'unavailable') return null;
  return liveCompareResult;
}

/** The five assumption-chip values, mutable `draft` — this scenario's
 * CURRENT lifecycle assumptions — or frozen from the snapshot as they
 * stood the moment it was approved. Kept in lockstep with
 * `resolveEffectiveCompareResult` above so the chip strip and the dollars
 * beside it can never cite two different points in time. */
export function resolveChipAssumptions(
  view: ApprovedCompareView,
  draft: Pick<ScenarioDraft, 'horizonMonths' | 'fmvBand' | 'kwhUsd' | 'pue' | 'itadRecoveryPct'>,
): Pick<ScenarioDraft, 'horizonMonths' | 'fmvBand' | 'kwhUsd' | 'pue' | 'itadRecoveryPct'> {
  if (view.kind !== 'frozen') return draft;
  const { horizonMonths, fmvBand, kwhUsd, pue, itadRecoveryPct } = view.snapshot;
  return { horizonMonths, fmvBand, kwhUsd, pue, itadRecoveryPct };
}

/**
 * `GET /me` — the assumption table's ONLY source for PUE's "Declared"
 * tier (`customer_org.pue` / `.pueUpdatedAt`, migration 708 — "the only
 * assumption with a complete... audit trail" per
 * resources/plans/wayfinder-scenario-assumption-sources.md). Deliberately
 * its own query key (`qk.me()`), not `qk.identity()`: that key is shared by
 * a narrower `fetchClientIdentity` shape elsewhere in the app.
 */
function useMeQuery() {
  return useQuery<MeResponse>({
    queryKey: qk.me(),
    queryFn: async () => unwrap<MeResponse>(await api.api.client.me.$get()),
  });
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

const STAGES: { label: string; hint: string }[] = [
  { label: 'Define paths', hint: 'Lifecycle actions and replacements' },
  { label: 'Review assumptions', hint: 'Confirm sources and overrides' },
  { label: 'Compare & decide', hint: 'Tradeoffs, carbon lens, evidence' },
];
/** The terminal stage, named once so the share link and the Next/Compute
 * switch below can never drift out of sync with STAGES' length again. */
const COMPARE_STAGE: Stage = 3;
const HORIZONS: ScenarioHorizonMonths[] = [12, 24, 36, 60];
const SAVE_ERROR_TITLE = "Couldn't save the scenario.";
const COMPUTE_ERROR_TITLE = "Couldn't compute the comparison.";

function ScenarioWizardPage() {
  const { scenarioId } = Route.useParams();
  const { stage, tab, classId } = Route.useSearch();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const isNew = scenarioId === 'new';

  const scenarioQuery = useQuery({
    queryKey: qk.scenarios.detail(scenarioId),
    queryFn: async () =>
      unwrap<FinancialScenarioRow>(await api.api.client.scenarios[':id'].$get({ param: { id: scenarioId } })),
    enabled: !isNew,
  });

  // SIL-566: a brand-new draft opened via the ?classId= deep link starts
  // with one cohort pre-scoped to that class instead of the usual empty
  // cohort list — read once (lazy initializer), never re-applied on a later
  // search change so it can't clobber cohorts the reader has since edited.
  const [draft, setDraft] = useState<ScenarioDraft>(() => {
    const base = emptyDraft();
    if (isNew && classId) {
      return { ...base, cohorts: [{ ...newCohortDraft(), scope: 'class', assetClassId: classId }] };
    }
    return base;
  });
  const [hydrated, setHydrated] = useState(isNew);
  const [savedId, setSavedId] = useState<string | null>(isNew ? null : scenarioId);
  const [compareResult, setCompareResult] = useState<ScenarioCompareResponse | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  // Which mutation the current saveError came from — titles the ErrorState
  // correctly and drives its retry (SIL-539's Voice rule wants `Couldn't
  // <thing>.`, not one generic banner for two different failures).
  const [saveErrorTitle, setSaveErrorTitle] = useState<string>(SAVE_ERROR_TITLE);
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);

  /**
   * SIL-441 — hypothetical buys and relocations. Deliberately OUTSIDE
   * `draft`: `draft` is what gets JSON-compared for the dirty flag and
   * shipped to `PUT /scenarios/:id`, and these have no persisted home
   * (`buy`/`move` are not `scenario_action` values — they are request-scoped
   * carbon inputs on the compare call). Folding them into `draft` would
   * mark the scenario dirty over something no save can ever persist,
   * leaving "Unsaved changes" stuck on screen forever. SIL-547 moved their
   * editor from a stage of its own to a "carbon lens" disclosure on stage 3
   * (Compare & decide) — the session-scoped nature is unchanged.
   */
  const [carbonBuys, setCarbonBuys] = useState<CarbonBuyDraft[]>([]);
  const [carbonMoves, setCarbonMoves] = useState<CarbonMoveDraft[]>([]);
  const [showCarbonLensEditor, setShowCarbonLensEditor] = useState(false);

  /**
   * SIL-547 — discount rate. Confirmed absent from
   * `db/schema/*.sql`/`packages/shared/src`
   * (resources/plans/wayfinder-scenario-assumption-sources.md: "does not
   * exist... ships as an explicitly user-entered assumption"). It has no
   * persisted home and no term in `scenarioEngine`, so — same posture as
   * `carbonBuys`/`carbonMoves` above — it lives in component state only,
   * never sent to the API, and is never counted as affecting the dollar
   * series. The assumption table renders it because SIL-547 (issue) says
   * the table's rows are `SENSITIVITY_ATTRIBUTES` PLUS discount rate, not
   * because it does anything yet.
   */
  const [discountRatePct, setDiscountRatePct] = useState<number | null>(null);

  // ponytail: JSON compare instead of a deep-equal dependency — every draft is
  // built by emptyDraft / draftFromRow / newCohortDraft, so key order is stable.
  const [savedSnapshot, setSavedSnapshot] = useState<string>(() => JSON.stringify(emptyDraft()));
  const isDirty = JSON.stringify(draft) !== savedSnapshot;

  /**
   * Single-flight guard for the FIRST save of a `'new'` draft (see
   * `lib/singleFlight.ts`'s docstring for the full mechanism). `savedId`
   * only settles once `saveMutation`'s `onSuccess` runs, and stage nav /
   * Back / Compute / Save all independently call
   * `saveMutation.mutateAsync()` whenever the draft is dirty — so without
   * this, two of those triggered close enough together (well within a
   * network round trip, no double-click required) both observe
   * `savedId === null` and each POST a new scenario row. One instance per
   * mounted draft (`useRef`, not module scope) — a second `'new'` tab is a
   * separate, intentional draft, not a race to dedupe.
   */
  const createSingleFlightRef = useRef(new SingleFlight<FinancialScenarioRow>());

  /**
   * Cohorts timed past the horizon. The API rejects this (it would model as a
   * silent hold), which means autosave CANNOT run while it is true. SIL-547
   * moved the horizon control to the page header (next to the cohort-timing
   * stage it validates, instead of two stages away) precisely so this can
   * never trap the reader again — see the module docstring. So an invalid
   * draft navigates freely and stays dirty; `beforeunload` and the two
   * inline warnings (per-cohort on stage 1, aggregate in the header) carry
   * the risk instead.
   */
  const strandedCohorts = draft.cohorts.filter((c) => isStranded(c, draft.horizonMonths));
  const canAutosave = strandedCohorts.length === 0;

  /**
   * Classes already referenced by the draft, passed to the endpoint as
   * `includeIds` so they come back even if the org no longer owns anything
   * in them. Without this, reopening an older scenario would render a blank
   * "Asset class" select over a cohort that IS set — and the next autosave
   * (or any unrelated edit) would quietly rewrite the scenario to match the
   * blank. Sorted for a stable query key.
   */
  const ownedClassIncludeIds = useMemo(
    () =>
      Array.from(
        new Set(draft.cohorts.map((c) => c.assetClassId).filter((id): id is string => !!id)),
      ).sort(),
    [draft.cohorts],
  );

  /**
   * SIL — owned holdings, not the full taxonomy. See `AssetClassRef`.
   * Deliberately NOT `qk.reference()`: this is org-scoped and include-ids
   * dependent, and sharing that cache key would poison every other consumer
   * of the catalog reference (profile catalog preferences, asset submission)
   * with a three-entry list.
   */
  const ownedClassesQuery = useQuery({
    queryKey: qk.ownedAssetClasses(ownedClassIncludeIds),
    queryFn: async () =>
      unwrap<{ assetClasses: AssetClassRef[] }>(
        await api.api.client.reference['owned-asset-classes'].$get({
          query: ownedClassIncludeIds.length ? { includeIds: ownedClassIncludeIds.join(',') } : {},
        }),
      ),
    // A draft hydrating from the server briefly has zero cohorts; refetching
    // once the real include-ids arrive is one extra request, and keeping the
    // previous rows on screen avoids the select flickering to empty.
    placeholderData: (prev) => prev,
  });
  const assetClasses = ownedClassesQuery.data?.assetClasses ?? [];
  /** True only once the fetch has actually resolved — an in-flight query has
   * an empty list too, and "you own nothing" is the wrong thing to say to
   * someone who is still loading. */
  const ownsNothing = ownedClassesQuery.isSuccess && assetClasses.length === 0;

  // Epic 2 (lots-first-class) — lots + teams for the "By lot"/"By team" cohort
  // scope selects. Mirrors apps/customer/src/routes/assets/index.tsx's lot
  // filter fetch (org-scoped list, not admin-gated — a member can build a
  // cohort scoped to any team in the org; visibility/RLS narrows what that
  // resolves to at compute time, same posture as `assetClassId`).
  const lotsQuery = useQuery<ScenarioLotOption[]>({
    queryKey: qk.lots.all,
    queryFn: async () => {
      const res = await api.api.client.lots.$get();
      await throwIfNotOk(res);
      return (await res.json()) as unknown as ScenarioLotOption[];
    },
  });
  const lots = lotsQuery.data ?? [];

  const teamsQuery = useQuery<ScenarioTeamOption[]>({
    queryKey: qk.teams(),
    queryFn: async () => {
      const res = await api.api.client.teams.$get();
      await throwIfNotOk(res);
      return (await res.json()) as unknown as ScenarioTeamOption[];
    },
  });
  const teams = teamsQuery.data ?? [];

  useEffect(() => {
    if (!isNew && scenarioQuery.data && !hydrated) {
      const hydratedDraft = draftFromRow(scenarioQuery.data);
      setDraft(hydratedDraft);
      setSavedSnapshot(JSON.stringify(hydratedDraft));
      setSavedId(scenarioQuery.data.id);
      setHydrated(true);
    }
  }, [isNew, scenarioQuery.data, hydrated]);

  // Last line of defence for a reload or tab close. Autosave (below) covers
  // in-app navigation; this covers the case it structurally cannot see.
  useEffect(() => {
    if (!isDirty) return;
    const warn = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [isDirty]);

  /**
   * Persists the draft before moving, and does NOT move if that fails —
   * navigating on a failed save would lose the work AND leave the reader
   * somewhere else, which is the worst of both.
   *
   * The draft lives only in React state, so every navigation out of a stage
   * used to discard it silently: Next, Back, the stage indicator, and the
   * breadcrumb. Saving here also resolves the `'new'` id, so a reload on
   * stage 2 of a new scenario no longer comes back to an empty draft.
   *
   * A TAB switch inside stage 1 (`goTab` below) does NOT go through this —
   * only a stage change does. Tabs are freely navigable with no gate.
   */
  const goStage = async (n: Stage) => {
    let id = savedId;
    if (isDirty && canAutosave) {
      try {
        id = (await saveMutation.mutateAsync()).id;
      } catch {
        return; // saveError is already set and rendered
      }
    }
    void navigate({
      to: '/financials/scenarios/$scenarioId',
      params: { scenarioId: id ?? 'new' },
      // `tab` is inert outside stage 1 (only the stage-1 branch below reads
      // it) — the share URL already omits it; internal nav now agrees.
      search: n === 1 ? { stage: n, tab } : { stage: n },
      replace: true,
    });
  };

  const goTab = (n: Stage1Tab) => {
    void navigate({
      to: '/financials/scenarios/$scenarioId',
      params: { scenarioId: savedId ?? 'new' },
      search: { stage: 1, tab: n },
      replace: true,
    });
  };

  const refreshCohorts = useMemo(
    () => draft.cohorts.filter((c) => c.action === 'refresh' && c.scope === 'class' && c.assetClassId),
    [draft.cohorts],
  );
  // SIL-571 — Purchases tab (stage 1's third tab): every `buy` cohort, no
  // class-scope requirement (a buy cohort doesn't select existing owned
  // assets — its `selector` is inert, see toCohortWriteBody's docstring).
  const purchaseCohorts = useMemo(() => draft.cohorts.filter((c) => c.action === 'buy'), [draft.cohorts]);

  /**
   * ONE request, always. This used to be a PATCH plus a DELETE per removed
   * cohort plus a PATCH/POST per remaining cohort, orchestrated from the
   * browser with no transaction: a failure part-way through left the scenario
   * half-written, and the error told the reader neither which cohort failed
   * nor what had already persisted — each request carried its own transaction,
   * so each committed on its own. `PUT /scenarios/:id` applies the whole replace
   * in one request, so a save either lands completely or not at all.
   */
  const saveMutation = useMutation({
    mutationFn: async () => {
      setSaveError(null);
      const fields = {
        name: draft.name.trim() || 'Untitled scenario',
        horizonMonths: draft.horizonMonths,
        fmvBand: draft.fmvBand,
        kwhUsd: draft.kwhUsd,
        pue: draft.pue,
        itadRecoveryPct: draft.itadRecoveryPct,
      };

      if (!savedId) {
        // See createSingleFlightRef's docstring above — this is the fix for
        // the duplicate-scenario-row bug: a second caller that arrives
        // while this POST is still in flight shares this exact promise
        // instead of issuing its own POST, so a single logical draft can
        // never mint two rows no matter how it's triggered.
        return createSingleFlightRef.current.runOnce(async () =>
          unwrap<FinancialScenarioRow>(
            await api.api.client.scenarios.$post({
              json: { ...fields, cohorts: draft.cohorts.map(toCohortWriteBody) },
            }),
          ),
        );
      }

      return unwrap<FinancialScenarioRow>(
        await api.api.client.scenarios[':id'].$put({
          param: { id: savedId },
          json: {
            ...fields,
            // `id` present = update that cohort, absent = insert; any stored
            // cohort missing from this array is deleted server-side.
            cohorts: draft.cohorts.map((c) => ({
              ...toCohortWriteBody(c),
              ...(c.id ? { id: c.id } : {}),
            })),
          },
        }),
      );
    },
    onSuccess: (row) => {
      const savedDraft = draftFromRow(row);
      setDraft(savedDraft);
      setSavedSnapshot(JSON.stringify(savedDraft));
      setSavedId(row.id);
      // SIL-568 — an edit to an approved scenario un-approves it server-side
      // (financial_scenario_lock_trg, mig 728); the response is the ONLY
      // signal the un-approve fired (the record is already cleared by the
      // time this resolves), so compare against the pre-save cached row to
      // decide whether to surface the voided-approval banner.
      const prevStatus = scenarioQuery.data?.status;
      const prevApprovedAt = scenarioQuery.data?.approvedAt;
      if (prevStatus === 'approved' && row.status === 'draft' && prevApprovedAt) {
        setVoidedBanner({ approvedAt: prevApprovedAt });
        // The voided approval's snapshot query goes `enabled: false` on its
        // own once `scenarioQuery` refetches to `draft` (below), but the
        // stale `available`/`unavailable` payload from the just-voided
        // approval would otherwise sit in cache until then — invalidate it
        // explicitly so a citation banner can never outlive the approval it
        // cites.
        void qc.invalidateQueries({ queryKey: qk.scenarios.approvalSnapshot(row.id) });
      }
      void qc.invalidateQueries({ queryKey: qk.scenarios.all });
      void qc.invalidateQueries({ queryKey: qk.scenarios.detail(row.id) });
      if (isNew) {
        void navigate({
          to: '/financials/scenarios/$scenarioId',
          params: { scenarioId: row.id },
          search: stage === 1 ? { stage, tab } : { stage },
          replace: true,
        });
      }
    },
    onError: (err: unknown) => {
      setSaveError(err instanceof Error ? err.message : 'Failed to save scenario');
      setSaveErrorTitle(SAVE_ERROR_TITLE);
    },
  });

  // ---------------------------------------------------------------------------
  // SIL-568 — approval workflow. `status` is never part of `draft` (it's not
  // client-writable, spec §3.2) — these three transition endpoints are the
  // only way it changes, so they're separate mutations from `saveMutation`
  // above, each invalidating the same detail query.
  // ---------------------------------------------------------------------------
  const meQ = useMeQuery();
  const [voidedBanner, setVoidedBanner] = useState<{ approvedAt: string } | null>(null);

  const submitMutation = useMutation({
    mutationFn: async () =>
      unwrap<FinancialScenarioRow>(
        await api.api.client.scenarios[':id'].submit.$post({ param: { id: savedId ?? '' } }),
      ),
    onSuccess: (row) => {
      void qc.invalidateQueries({ queryKey: qk.scenarios.detail(row.id) });
      toast.success('Submitted for approval.');
    },
    onError: (err: Error & { body?: { error?: { message?: string } } }) => {
      toast.error(err.body?.error?.message ?? err.message ?? 'Could not submit for approval.');
    },
  });

  const approveMutation = useMutation({
    mutationFn: async () =>
      unwrap<FinancialScenarioRow>(
        await api.api.client.scenarios[':id'].approve.$post({ param: { id: savedId ?? '' } }),
      ),
    onSuccess: (row) => {
      void qc.invalidateQueries({ queryKey: qk.scenarios.detail(row.id) });
      // `POST /:id/approve` writes a NEW approval-snapshot version in the
      // same transaction (mig 748) — the previous fetch (if any, from a
      // prior approval on this same scenario) is now stale by construction.
      // Without this, a re-approve after Withdraw → edit → Submit → Approve
      // could leave the OLD version's cached response on screen until
      // something else happened to invalidate it.
      void qc.invalidateQueries({ queryKey: qk.scenarios.approvalSnapshot(row.id) });
      toast.success('Scenario approved.');
    },
    onError: (err: Error & { body?: { error?: { message?: string } } }) => {
      toast.error(err.body?.error?.message ?? err.message ?? 'Could not approve.');
    },
  });

  const unapproveMutation = useMutation({
    mutationFn: async () =>
      unwrap<FinancialScenarioRow>(
        await api.api.client.scenarios[':id'].unapprove.$post({ param: { id: savedId ?? '' } }),
      ),
    onSuccess: (row) => {
      void qc.invalidateQueries({ queryKey: qk.scenarios.detail(row.id) });
      // Same reasoning as approveMutation above — status flips away from
      // `approved` so the query goes `enabled: false`, but drop the cached
      // payload immediately rather than leaving it to linger.
      void qc.invalidateQueries({ queryKey: qk.scenarios.approvalSnapshot(row.id) });
      toast.success('Approval withdrawn.');
    },
    onError: (err: Error & { body?: { error?: { message?: string } } }) => {
      toast.error(err.body?.error?.message ?? err.message ?? 'Could not withdraw the approval.');
    },
  });

  // ---------------------------------------------------------------------------
  // SIL-570 (I2-C3) — "Save as template". Fail-closed server-side: any
  // cohort whose selector selects specific asset ids (rather than a
  // class/all predicate) 422s the WHOLE save and names the offending
  // cohort (spec §3.1) — surfaced here verbatim, not summarized away.
  // ---------------------------------------------------------------------------
  const saveAsTemplateMutation = useMutation({
    mutationFn: async (name: string) =>
      unwrap<{ id: string; name: string }>(
        await api.api.client.scenarios[':id']['save-as-template'].$post({
          param: { id: savedId ?? '' },
          json: { name },
        }),
      ),
    onSuccess: (row) => {
      toast.success(`Saved template "${row.name}". Find it under Scenarios > Start from a template.`);
    },
    onError: (err: Error & { body?: { error?: { message?: string } } }) => {
      toast.error(err.body?.error?.message ?? err.message ?? 'Could not save this scenario as a template.');
    },
  });

  const computeMutation = useMutation({
    mutationFn: async () => {
      // SIL-522 — a view should not write: this used to save unconditionally,
      // so merely opening the Compare stage on an already-saved, untouched
      // scenario fired a `PUT /scenarios/:id` with nothing for the reader to
      // have changed. Only save when the draft actually diverges from what's
      // persisted; otherwise reuse `savedId` (`ready` above already requires
      // `hydrated`, so it's populated by the time this can run).
      const id = isDirty && canAutosave ? (await saveMutation.mutateAsync()).id : savedId;
      if (!id) throw new Error('Scenario not saved yet');
      // The ONLY place the hypotheticals reach the server — request-scoped,
      // never persisted. Incomplete rows are dropped by
      // `toCarbonCohortInputs` rather than 400ing the whole compute.
      const carbonCohorts = toCarbonCohortInputs(carbonBuys, carbonMoves);
      return unwrap<ScenarioCompareResponse>(
        await api.api.client.scenarios[':id'].compare.$post({
          param: { id },
          json: carbonCohorts ? { carbonCohorts } : {},
        }),
      );
    },
    onSuccess: (result) => {
      setSaveError(null);
      setCompareResult(result);
    },
    onError: (err: unknown) => {
      setSaveError(err instanceof Error ? err.message : 'Failed to compute comparison');
      setSaveErrorTitle(COMPUTE_ERROR_TITLE);
    },
  });

  /**
   * A share link points at `?stage=3` of a saved scenario, and `compareResult`
   * is React state with no persisted home — so without this the recipient
   * lands on the Compare stage's empty state instead of the comparison that
   * was shared. Gated on the scenario having hydrated (the compute posts the
   * draft, which is `emptyDraft()` until then) and on `canAutosave` (a
   * stranded-cohort draft is rejected by the API). Also gated off an
   * `approved` scenario: the Compare & decide page cites the frozen
   * approval snapshot for one, never a live recompute, so auto-firing a
   * `/compare` request whose result would never be shown is pure waste —
   * see `resolveApprovedCompareView` above.
   */
  useScenarioAutoCompute({
    ready:
      stage === COMPARE_STAGE && !isNew && hydrated && canAutosave && scenarioQuery.data?.status !== 'approved',
    hasResult: compareResult !== null,
    isComputing: computeMutation.isPending,
    onCompute: () => void computeMutation.mutate(),
  });

  const page = (
    <div className="mx-auto max-w-4xl space-y-6">
      <div>
        {/* Saves before leaving when dirty; stays a plain link otherwise, so
            cmd-click and right-click keep working on a clean draft. */}
        <BackLink
          as={Link}
          to="/financials/scenarios"
          onClick={(e) => {
            if (!isDirty || !canAutosave || e.metaKey || e.ctrlKey || e.shiftKey) return;
            e.preventDefault();
            void (async () => {
              try {
                await saveMutation.mutateAsync();
              } catch {
                return;
              }
              void navigate({ to: '/financials/scenarios' });
            })();
          }}
        >
          Scenarios
        </BackLink>
        {/* No page-level <h1> existed before this — heading navigation
            landed nowhere on a route with a CFO-facing dollar delta.
            The editable title stays an input (it hosts inline rename,
            which a plain <h1> can't), so the heading is a visually-hidden
            sibling naming the same text for the accessibility tree. */}
        <h1 className="sr-only">{draft.name.trim() || 'Untitled scenario'} — scenario</h1>
        {/* A plain string-title PageHeader can't host an inline-editable
            input, so this stays a hand-built two-line header (title row +
            status subtitle) rather than <PageHeader> — SIL-544's call,
            unchanged by this restructure. SIL-547 adds the horizon control
            to the subtitle row: it validates cohort timing (stage 1), so it
            now sits zero stages away instead of two. */}
        <input
          value={draft.name}
          onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
          className="mt-1 block w-full border-none bg-transparent p-0 text-xl font-semibold text-foreground outline-none focus:ring-0"
          aria-label="Scenario name"
        />
        <div className="mt-0.5 flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
          <label className="inline-flex items-center gap-1.5">
            <span>Horizon</span>
            <select
              value={draft.horizonMonths}
              onChange={(e) =>
                setDraft((d) => ({ ...d, horizonMonths: Number(e.target.value) as ScenarioHorizonMonths }))
              }
              className="rounded border border-border bg-transparent px-1 py-0.5 text-xs text-foreground"
              aria-label="Scenario horizon"
            >
              {HORIZONS.map((h) => (
                <option key={h} value={h}>
                  {h} months
                </option>
              ))}
            </select>
          </label>
          <span aria-hidden>·</span>
          <span>{isDirty ? 'Unsaved changes — autosaves as you edit.' : 'All changes saved.'}</span>
        </div>
        {/* The horizon/cohort-timing constraint, now directly under the
            control that causes it — see the module docstring's "trap"
            note. The per-cohort echo of this same warning still renders on
            stage 1 (Lifecycle paths), next to the timing field it explains. */}
        {strandedCohorts.length > 0 && (
          <div className="mt-2 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-2.5 py-2 text-xs text-amber-800">
            <AlertTriangle className="mt-0.5 size-3.5 shrink-0 text-amber-600" />
            <span>
              {strandedCohorts.length === 1
                ? `Cohort "${strandedCohorts[0]?.label}" acts`
                : `${strandedCohorts.length} cohorts act`}{' '}
              after month {draft.horizonMonths} and would model as a hold. Fix the timing on Define
              paths → Lifecycle paths, or keep a longer horizon.
            </span>
          </div>
        )}
      </div>

      <StageIndicator stage={stage} onStage={goStage} />

      {!isNew && savedId && (
        <div className="flex justify-end">
          <Button
            size="sm"
            variant="secondary"
            disabled={saveAsTemplateMutation.isPending}
            onClick={() => setTemplateDialogOpen(true)}
          >
            Save as template
          </Button>
          {templateDialogOpen && (
            <NameTemplateDialog
              defaultName={`${draft.name} template`}
              isPending={saveAsTemplateMutation.isPending}
              onCancel={() => setTemplateDialogOpen(false)}
              onSubmit={(name) => {
                saveAsTemplateMutation.mutate(name, { onSuccess: () => setTemplateDialogOpen(false) });
              }}
            />
          )}
        </div>
      )}

      {!isNew && savedId && scenarioQuery.data && (
        <ScenarioTeamOwnership
          scenarioId={savedId}
          teamId={scenarioQuery.data.teamId}
          teamName={scenarioQuery.data.teamName}
          teams={teams}
          isAdmin={meQ.data?.role === 'admin'}
        />
      )}

      {!isNew && savedId && (
        <ApprovalBar
          row={scenarioQuery.data}
          isAdmin={meQ.data?.role === 'admin'}
          voidedBanner={voidedBanner}
          onDismissVoided={() => setVoidedBanner(null)}
          onSubmit={() => submitMutation.mutate()}
          onApprove={() => approveMutation.mutate()}
          onUnapprove={() => unapproveMutation.mutate()}
          isSubmitting={submitMutation.isPending}
          isApproving={approveMutation.isPending}
          isUnapproving={unapproveMutation.isPending}
        />
      )}

      {saveError && (
        <ErrorState
          title={saveErrorTitle}
          error={saveError}
          onRetry={() => {
            setSaveError(null);
            if (saveErrorTitle === COMPUTE_ERROR_TITLE) void computeMutation.mutate();
            else void saveMutation.mutate();
          }}
        />
      )}

      {stage === 1 && (
        <div className="space-y-4">
          <div role="tablist" aria-label="Define paths" className="flex gap-1 border-b border-border">
            {(['lifecycle', 'replacements', 'purchases'] as Stage1Tab[]).map((t) => (
              <button
                key={t}
                type="button"
                role="tab"
                id={`stage1-tab-${t}`}
                aria-selected={tab === t}
                aria-controls={`stage1-panel-${t}`}
                tabIndex={tab === t ? 0 : -1}
                onKeyDown={(e) => {
                  if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
                  e.preventDefault();
                  const order: Stage1Tab[] = ['lifecycle', 'replacements', 'purchases'];
                  const i = order.indexOf(tab ?? 'lifecycle');
                  const next = order[(i + (e.key === 'ArrowRight' ? 1 : order.length - 1)) % order.length]!;
                  goTab(next);
                  document.getElementById(`stage1-tab-${next}`)?.focus();
                }}
                onClick={() => goTab(t)}
                className={cn(
                  '-mb-px border-b-2 px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500',
                  tab === t
                    ? 'border-brand-600 font-medium text-foreground'
                    : 'border-transparent text-muted-foreground hover:text-foreground',
                )}
              >
                {t === 'lifecycle' ? 'Lifecycle paths' : t === 'replacements' ? 'Replacements' : 'Purchases'}
              </button>
            ))}
          </div>

          <div role="tabpanel" id={`stage1-panel-${tab}`} aria-labelledby={`stage1-tab-${tab}`}>
          {tab === 'lifecycle' ? (
            <CohortsStep
              cohorts={draft.cohorts}
              assetClasses={assetClasses}
              lots={lots}
              teams={teams}
              ownsNothing={ownsNothing}
              scenarioId={savedId}
              horizonMonths={draft.horizonMonths}
              onChange={(cohorts) => setDraft((d) => ({ ...d, cohorts }))}
            />
          ) : tab === 'replacements' ? (
            <RefreshStep
              cohorts={refreshCohorts}
              allCohorts={draft.cohorts}
              assetClasses={assetClasses}
              onChange={(cohorts) => setDraft((d) => ({ ...d, cohorts }))}
            />
          ) : (
            <PurchasesStep
              cohorts={purchaseCohorts}
              allCohorts={draft.cohorts}
              assetClasses={assetClasses}
              onChange={(cohorts) => setDraft((d) => ({ ...d, cohorts }))}
            />
          )}
          </div>
        </div>
      )}

      {stage === 2 && (
        <AssumptionTable
          draft={draft}
          onChange={(patch) => setDraft((d) => ({ ...d, ...patch }))}
          scenarioId={savedId}
          discountRatePct={discountRatePct}
          onChangeDiscountRatePct={setDiscountRatePct}
          // SIL-532 (I2-C2) — same staleness read model the Overview
          // counter and scenario-list badges consume; undefined for an
          // unsaved draft (nothing to compare against yet).
          staleness={scenarioQuery.data?.staleness}
        />
      )}

      {stage === COMPARE_STAGE && (
        <CompareStep
          draft={draft}
          compareResult={compareResult}
          isComputing={computeMutation.isPending}
          onCompute={() => void computeMutation.mutate()}
          shareUrl={
            savedId
              ? `${window.location.origin}/financials/scenarios/${savedId}?stage=${COMPARE_STAGE}`
              : null
          }
          scenarioId={savedId}
          assetClasses={assetClasses}
          lots={lots}
          teams={teams}
          carbonBuys={carbonBuys}
          carbonMoves={carbonMoves}
          onChangeCarbonBuys={setCarbonBuys}
          onChangeCarbonMoves={setCarbonMoves}
          showCarbonLensEditor={showCarbonLensEditor}
          onToggleCarbonLensEditor={() => setShowCarbonLensEditor((s) => !s)}
          discountRatePct={discountRatePct}
          isDirty={isDirty}
          canAutosave={canAutosave}
          scenarioRow={scenarioQuery.data}
        />
      )}

      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border pt-4">
        <button
          type="button"
          disabled={stage === 1 || saveMutation.isPending}
          onClick={() => void goStage((stage - 1) as Stage)}
          className="rounded-md border border-border px-3 py-2 text-sm text-muted-foreground disabled:opacity-40"
        >
          Back
        </button>
        <div className="flex items-center gap-3">
          {/* State of the draft, stated plainly. Without this the reader had no
              way to tell whether their work was persisted. */}
          <span
            className={`text-xs ${canAutosave ? 'text-muted-foreground' : 'text-amber-700'}`}
            aria-live="polite"
          >
            {saveMutation.isPending
              ? 'Saving…'
              : !canAutosave
                ? `Can't save — ${strandedCohorts.length === 1 ? '1 cohort acts' : `${strandedCohorts.length} cohorts act`} past the ${draft.horizonMonths}-month horizon`
                : isDirty
                  ? 'Unsaved changes'
                  : savedId
                    ? 'All changes saved'
                    : 'Not saved yet'}
          </span>
          {/* Primary while dirty: the committing action on this screen is
              persisting the work, not advancing a stage. */}
          <Button
            variant={isDirty ? 'primary' : 'secondary'}
            className="px-3"
            onClick={() => void saveMutation.mutate()}
            disabled={saveMutation.isPending || !isDirty || !canAutosave}
          >
            {saveMutation.isPending ? 'Saving…' : 'Save'}
          </Button>
          {stage < COMPARE_STAGE ? (
            <Button
              variant={isDirty ? 'secondary' : 'primary'}
              className="px-3"
              disabled={saveMutation.isPending}
              onClick={() => void goStage((stage + 1) as Stage)}
            >
              Next
            </Button>
          ) : (
            <Button className="px-3"
              onClick={() => void computeMutation.mutate()}
              disabled={computeMutation.isPending || !canAutosave}
            >
              {computeMutation.isPending ? 'Computing…' : 'Compute comparison'}
            </Button>
          )}
        </div>
      </div>
    </div>
  );

  // `scenarioQuery` is permission/existence-gated (`enabled: !isNew`) — per
  // the QueryBoundary Adoption Contract's documented trap, it needs its own
  // outer gate here or a brand-new (`isNew`) draft's never-run query would
  // render the loading skeleton forever instead of the fresh-draft page.
  if (!isNew) {
    return (
      <QueryBoundary
        query={scenarioQuery}
        errorTitle="Couldn't load scenario."
        loading={
          <div className="mx-auto max-w-4xl">
            <LoadingShell rows={5} />
          </div>
        }
      >
        {() => page}
      </QueryBoundary>
    );
  }
  return page;
}

// ---------------------------------------------------------------------------
// Stage indicator — roving-tabindex, arrow-key navigable, every stage
// always reachable (SIL-547 kept "click any stage"; this adds keyboard
// parity + accessible names for completed steps, which previously swapped
// their number for an unlabelled check glyph).
// ---------------------------------------------------------------------------

export function StageIndicator({ stage, onStage }: { stage: Stage; onStage: (n: Stage) => void }) {
  // Roving tabindex: only the active step is a tab stop; arrow keys move
  // both focus and selection. Previously every step was its own tab stop
  // with the Chrome UA default focus ring (DESIGN.md: the emerald ring IS
  // the focus expression) and no keyboard behavior at all.
  const move = (from: Stage, dir: 1 | -1) => {
    const next = (((from - 1 + dir + STAGES.length) % STAGES.length) + 1) as Stage;
    onStage(next);
    document.getElementById(`wizard-stage-${next}`)?.focus();
  };
  return (
    <nav aria-label="Scenario builder stages">
      <ol className="flex items-center gap-0">
        {STAGES.map((s, i) => {
          const n = (i + 1) as Stage;
          const done = stage > n;
          const active = stage === n;
          return (
            <li key={s.label} className="flex flex-1 items-center">
              <button
                id={`wizard-stage-${n}`}
                type="button"
                aria-current={active ? 'step' : undefined}
                // A completed step swapping its number for a bare check
                // glyph left the accessible name empty — name it explicitly
                // regardless of what's visually inside the circle.
                aria-label={`${s.label}${done ? ' (completed)' : active ? ' (current step)' : ''} — ${s.hint}`}
                tabIndex={active ? 0 : -1}
                onKeyDown={(e) => {
                  if (e.key === 'ArrowRight') {
                    e.preventDefault();
                    move(stage, 1);
                  } else if (e.key === 'ArrowLeft') {
                    e.preventDefault();
                    move(stage, -1);
                  }
                }}
                onClick={() => onStage(n)}
                className="flex flex-1 items-center gap-2.5 rounded-md text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
              >
                <div
                  aria-hidden="true"
                  className={cn(
                    'flex size-7 shrink-0 items-center justify-center rounded-full text-xs font-semibold',
                    done
                      ? 'bg-brand-600 text-white'
                      : active
                        ? 'border-2 border-brand-600 text-brand-700'
                        : 'border border-border text-muted-foreground',
                  )}
                >
                  {done ? <Check className="size-3.5" /> : n}
                </div>
                <div>
                  <p className={cn('text-sm', active ? 'font-medium text-foreground' : 'text-muted-foreground')}>
                    {s.label}
                  </p>
                  <p className="text-2xs text-muted-foreground">{s.hint}</p>
                </div>
              </button>
              {i < STAGES.length - 1 && <div aria-hidden="true" className="mx-2 h-px flex-1 bg-border" />}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

// ---------------------------------------------------------------------------
// Stage 1, tab "Lifecycle paths" — Baseline (synthetic) + exception cohorts
// ---------------------------------------------------------------------------

/**
 * Option label for an owned-class select. `assetCount === 0` can only happen
 * for a class kept in the list by `includeIds` — i.e. one an older saved
 * cohort still points at. Labelling it rather than dropping it keeps the
 * saved selection visible and selected, and tells the reader why the cohort
 * is about to compute as empty instead of leaving them to discover it on the
 * Compare stage.
 */
export function classOptionLabel(ac: AssetClassRef): string {
  return ac.assetCount > 0 ? `${ac.name} (${ac.assetCount})` : `${ac.name} — none owned`;
}

const ACTIONS: { value: ScenarioAction; label: string }[] = [
  { value: 'hold', label: 'Hold' },
  { value: 'sell', label: 'Sell' },
  { value: 'itad', label: 'ITAD (recycle)' },
  { value: 'refresh', label: 'Refresh (replace)' },
  { value: 'buy', label: 'Buy (new purchase)' },
];

/** Exported for `__tests__/owned-asset-class-pickers.test.tsx` — the owned-only
 * class list and its empty state are the contract this step now carries, and
 * mounting the whole wizard route (router + query client + hc mock) to reach
 * one select would test the harness more than the behaviour. */
export function CohortsStep({
  cohorts,
  assetClasses,
  lots,
  teams,
  ownsNothing,
  scenarioId,
  horizonMonths,
  onChange,
}: {
  cohorts: CohortDraft[];
  /** OWNED classes only (plus any already-selected class kept visible) — see
   * `AssetClassRef`. A class-scoped cohort over a class the org owns nothing
   * in is an empty cohort, so offering one is offering a dead end. */
  assetClasses: AssetClassRef[];
  /** Epic 2 (lots-first-class) — org-scoped lot list for the "By lot"
   * scope's select. */
  lots: ScenarioLotOption[];
  /** Epic 2 — org-scoped team list for the "By team" scope's select. */
  teams: ScenarioTeamOption[];
  /** The org owns no priced assets at all — the whole step is unusable, and
   * saying so beats four selects that each silently do nothing. */
  ownsNothing: boolean;
  /** SIL-306 (W4, spec slice 7) — null until the scenario has been saved at
   * least once; the "Draft exchange listing" affordance needs a persisted
   * scenario + cohort id to pass as sourceScenarioId/sourceCohortId. */
  scenarioId: string | null;
  /**
   * The scenario's horizon, now set in the page header. Timing is entered
   * here, so without it on screen the constraint between the two is invisible:
   * a cohort timed past the horizon behaves exactly like `hold` for the whole
   * run (scenarioEngine iterates months 0..horizonMonths), so the chosen action
   * silently never happens.
   */
  horizonMonths: ScenarioHorizonMonths;
  onChange: (cohorts: CohortDraft[]) => void;
}) {
  // SIL-522 — editing scope or asset class is the reader replacing this
  // cohort's membership, so it's the one place an `unmodeledSelector`
  // (round-tripped verbatim from a selector shape this form doesn't render,
  // e.g. `assetIds`) is allowed to be discarded in favor of the modeled
  // scope/class the reader just chose. Any other patch (label, action,
  // timing) leaves it untouched.
  const update = (key: string, patch: Partial<CohortDraft>) =>
    onChange(
      cohorts.map((c) => {
        if (c.key !== key) return c;
        const next = { ...c, ...patch };
        if ('scope' in patch || 'assetClassId' in patch || 'lotId' in patch || 'teamId' in patch) {
          next.unmodeledSelector = null;
        }
        return next;
      }),
    );
  // Names the cohort rather than asking "are you sure?" — a cohort carries a
  // scope, an action, a timing, and possibly a refresh SKU, so removing the
  // wrong one silently discards real configuration.
  const remove = (key: string) => {
    const target = cohorts.find((c) => c.key === key);
    const label = target?.label.trim() || 'this cohort';
    if (!window.confirm(`Remove the "${label}" cohort from this scenario?`)) return;
    onChange(cohorts.filter((c) => c.key !== key));
  };
  const add = () => onChange([...cohorts, newCohortDraft()]);

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Every asset follows the baseline (hold) unless it's included in one of the cohorts below.
        Split your portfolio into cohorts and pick an action for each — hold as-is, sell, send to
        ITAD, or refresh with a replacement SKU. A cohort is your whole portfolio, one asset class,
        one lot, or one team; a refresh cohort must resolve to a single asset class (consolidating
        mixed classes into one new SKU isn't a coherent action). Comparing two lots or two teams is
        two separate cohorts, not one cohort with multiple picked.
      </p>

      {ownsNothing && (
        <div
          data-testid="cohorts-owns-nothing"
          className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-2.5 py-2 text-xs text-amber-800"
        >
          <AlertTriangle className="mt-0.5 size-3.5 shrink-0 text-amber-600" />
          <span>
            No priced assets in your portfolio yet, so there are no asset classes to build a cohort
            from. Register assets — a class appears here once at least one of its assets has a
            current valuation.
          </span>
        </div>
      )}

      {/* SIL-547 — the baseline row: "rendered never stored", per the issue.
          Purely informational (no id, no form controls) — every asset the
          cohorts below don't claim implicitly follows this row. */}
      <div
        data-testid="baseline-cohort-row"
        className="flex items-center gap-3 rounded-lg border border-dashed border-border bg-muted/40 px-4 py-3"
      >
        <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-semibold text-muted-foreground">
          <Check className="size-3.5" />
        </span>
        <div className="flex-1">
          <p className="text-sm font-medium text-foreground">Baseline · Hold all assets</p>
          <p className="text-xs text-muted-foreground">
            Every asset not claimed by a cohort below remains in service through the horizon.
          </p>
        </div>
        <span className="text-2xs italic text-muted-foreground">synthetic row — nothing stored</span>
      </div>

      {cohorts.length === 0 && (
        <div className="rounded-md border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
          No exception cohorts yet.
        </div>
      )}

      <div className="space-y-3">
        {cohorts.map((c) => (
          <div key={c.key} className="rounded-lg border border-border bg-card p-4">
            <div className="flex items-start gap-3">
              <div className="grid flex-1 grid-cols-2 gap-3 sm:grid-cols-4">
                <label className="col-span-2 flex flex-col gap-1 text-xs text-muted-foreground sm:col-span-1">
                  Label
                  <input
                    value={c.label}
                    onChange={(e) => update(c.key, { label: e.target.value })}
                    className="rounded-md border border-border px-2 py-1.5 text-sm text-foreground"
                  />
                </label>
                <label className="flex flex-col gap-1 text-xs text-muted-foreground">
                  Scope
                  <select
                    value={c.scope}
                    onChange={(e) => {
                      const scope = e.target.value as 'all' | 'class' | 'lot' | 'team';
                      update(c.key, {
                        scope,
                        assetClassId: scope === 'class' ? (c.assetClassId ?? assetClasses[0]?.id ?? null) : null,
                        lotId: scope === 'lot' ? (c.lotId ?? lots[0]?.id ?? null) : null,
                        teamId: scope === 'team' ? (c.teamId ?? teams[0]?.id ?? null) : null,
                      });
                    }}
                    className="rounded-md border border-border px-2 py-1.5 text-sm text-foreground"
                  >
                    <option value="all">Whole portfolio</option>
                    <option value="class">By asset class</option>
                    <option value="lot">By lot</option>
                    <option value="team">By team</option>
                  </select>
                </label>
                {c.scope === 'class' && (
                  <label className="flex flex-col gap-1 text-xs text-muted-foreground">
                    Asset class
                    <select
                      value={c.assetClassId ?? ''}
                      onChange={(e) => update(c.key, { assetClassId: e.target.value || null })}
                      className="rounded-md border border-border px-2 py-1.5 text-sm text-foreground"
                    >
                      <option value="">Select…</option>
                      {assetClasses.map((ac) => (
                        <option key={ac.id} value={ac.id}>
                          {classOptionLabel(ac)}
                        </option>
                      ))}
                    </select>
                  </label>
                )}
                {/* Epic 2 — single-select lot per cohort. Comparing Lot A vs
                    Lot B is two cohort rows (each its own `select`), never a
                    multi-select here (plans/lots-first-class.md Epic 2 "One
                    lot per cohort"). */}
                {c.scope === 'lot' && (
                  <label className="flex flex-col gap-1 text-xs text-muted-foreground">
                    Lot
                    <select
                      value={c.lotId ?? ''}
                      onChange={(e) => update(c.key, { lotId: e.target.value || null })}
                      className="rounded-md border border-border px-2 py-1.5 text-sm text-foreground"
                    >
                      <option value="">Select…</option>
                      {lots.map((lot) => (
                        <option key={lot.id} value={lot.id}>
                          {lot.name}
                        </option>
                      ))}
                    </select>
                  </label>
                )}
                {c.scope === 'team' && (
                  <label className="flex flex-col gap-1 text-xs text-muted-foreground">
                    Team
                    <select
                      value={c.teamId ?? ''}
                      onChange={(e) => update(c.key, { teamId: e.target.value || null })}
                      className="rounded-md border border-border px-2 py-1.5 text-sm text-foreground"
                    >
                      <option value="">Select…</option>
                      {teams.map((team) => (
                        <option key={team.id} value={team.id}>
                          {team.name}
                        </option>
                      ))}
                    </select>
                  </label>
                )}
                <label className="flex flex-col gap-1 text-xs text-muted-foreground">
                  Action
                  <select
                    value={c.action}
                    onChange={(e) => {
                      const action = e.target.value as ScenarioAction;
                      const forceClass = action === 'refresh' || action === 'buy';
                      update(c.key, {
                        action,
                        // A refresh cohort must resolve to exactly one class
                        // (a lot/team can span classes — see
                        // `resolveCohortClassAggregates`'s multi-class
                        // rejection for `refresh`). A buy cohort's
                        // `scope`/`assetClassId` carry no economic meaning
                        // (buildBuyCohortInput ignores `selector` entirely)
                        // — forcing `class` here is purely so the Purchases
                        // tab's SKU picker has a class to filter candidates
                        // by (SIL-571).
                        scope: forceClass ? 'class' : c.scope,
                        assetClassId: forceClass ? (c.assetClassId ?? assetClasses[0]?.id ?? null) : c.assetClassId,
                        lotId: forceClass ? null : c.lotId,
                        teamId: forceClass ? null : c.teamId,
                      });
                    }}
                    className="rounded-md border border-border px-2 py-1.5 text-sm text-foreground"
                  >
                    {ACTIONS.map((a) => (
                      <option key={a.value} value={a.value}>
                        {a.label}
                      </option>
                    ))}
                  </select>
                </label>
                {/* The horizon is named in the label, not just enforced by
                    `max`, so the cap is explicable where it bites. */}
                <label className="flex flex-col gap-1 text-xs text-muted-foreground">
                  Timing (months, within {horizonMonths})
                  <input
                    type="number"
                    min={0}
                    max={horizonMonths}
                    value={c.timingMonths}
                    aria-describedby={
                      isStranded(c, horizonMonths) ? `timing-warn-${c.key}` : undefined
                    }
                    onChange={(e) =>
                      update(c.key, {
                        timingMonths: Math.min(
                          horizonMonths,
                          Math.max(0, Number(e.target.value) || 0),
                        ),
                      })
                    }
                    className="rounded-md border border-border px-2 py-1.5 text-sm text-foreground"
                  />
                </label>
              </div>
              <button
                type="button"
                aria-label={`Remove ${c.label}`}
                onClick={() => remove(c.key)}
                className="mt-5 rounded-md p-1.5 text-muted-foreground hover:bg-red-50 hover:text-red-600"
              >
                <Trash2 className="size-4" />
              </button>
            </div>
            {/* `max` cannot catch this: lowering the horizon in the header, or
                loading a scenario saved before the cap existed, leaves a timing
                already out of range. Say what it means rather than silently
                clamping the reader's number. */}
            {isStranded(c, horizonMonths) && (
              <div
                id={`timing-warn-${c.key}`}
                className="mt-2 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-2.5 py-2 text-xs text-amber-800"
              >
                <AlertTriangle className="mt-0.5 size-3.5 shrink-0 text-amber-600" />
                <span>
                  Acts at month {c.timingMonths}, past this scenario&apos;s {horizonMonths}-month
                  horizon — the{' '}
                  {ACTIONS.find((a) => a.value === c.action)?.label ?? c.action} would never happen,
                  so this cohort would be modelled as held for the whole run. Lower the timing, or
                  raise the horizon in the page header.
                </span>
              </div>
            )}
            {c.action === 'refresh' && (
              <div className="mt-2 text-xs text-muted-foreground">
                Pick the replacement SKU on the <span className="font-medium">Replacements</span> tab.
                {c.refreshSkuId ? ' Selected.' : ' Not selected yet.'}
              </div>
            )}
            {c.action === 'sell' && (
              <DraftListingWidget scenarioId={scenarioId} cohort={c} assetClasses={assetClasses} />
            )}
          </div>
        ))}
      </div>

      <button
        type="button"
        onClick={add}
        className="inline-flex items-center gap-1.5 rounded-md border border-dashed border-border px-3 py-2 text-sm text-muted-foreground hover:border-border hover:text-foreground"
      >
        <Plus className="size-4" /> Add cohort
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Scenario -> listing bridge (SIL-306, W4, spec slice 7) — "Draft exchange
// listing" on a sell cohort. `POST /api/client/listings` needs exactly one
// assetId (SIL-300's server-side pricing resolves the unit price from the
// COHORT's class aggregate, but the listing itself is still one asset), so
// this widget resolves a candidate asset in the cohort's class (letting the
// seller pick the class first when the cohort scope is 'all') via the same
// per-asset drill-down endpoint book-vs-fmv.tsx uses, then posts with
// sourceScenarioId/sourceCohortId — never a client-computed price.
// ---------------------------------------------------------------------------

function DraftListingWidget({
  scenarioId,
  cohort,
  assetClasses,
}: {
  scenarioId: string | null;
  cohort: CohortDraft;
  assetClasses: AssetClassRef[];
}) {
  const [open, setOpen] = useState(false);
  const [pickedClassId, setPickedClassId] = useState<string | null>(cohort.assetClassId);
  const [selectedAssetId, setSelectedAssetId] = useState('');
  const [result, setResult] = useState<Listing | null>(null);

  const assetsQ = useQuery<BookVsFmvAssetsResponse>({
    queryKey: qk.scenarios.listingCandidates(pickedClassId),
    queryFn: async () =>
      unwrap<BookVsFmvAssetsResponse>(
        await api.api.client.financials.summary.$get({
          query: { assetClassId: pickedClassId ?? '', limit: '50' },
        }),
      ),
    enabled: open && !!pickedClassId,
  });

  const draftMutation = useMutation({
    mutationFn: async () =>
      unwrap<Listing>(
        await api.api.client.listings.$post({
          json: {
            assetId: selectedAssetId,
            sourceScenarioId: scenarioId ?? undefined,
            sourceCohortId: cohort.id ?? undefined,
          },
        }),
      ),
    onSuccess: (listing) => {
      setResult(listing);
      toast.success('Draft exchange listing created.');
    },
    onError: (err: Error & { body?: { error?: { message?: string } } }) => {
      toast.error(err.body?.error?.message ?? err.message ?? 'Could not create the draft listing.');
    },
  });

  // Only a saved (persisted) cohort has an id the API can resolve.
  if (!scenarioId || !cohort.id) return null;

  if (result) {
    return (
      <div className="mt-2 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-800">
        Draft listing <span className="font-mono">{result.id.slice(0, 8)}</span> created — modeled{' '}
        {formatUsd(result.askingPriceUsd)}/unit. Realized price will be tracked against this
        scenario.{' '}
        <Link to="/listings" className="font-medium underline">
          View listings
        </Link>
      </div>
    );
  }

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="mt-2 inline-flex items-center gap-1.5 text-xs font-medium text-brand-600 hover:underline"
      >
        <Tag className="size-3.5" /> Draft exchange listing
      </button>
    );
  }

  return (
    <div className="mt-2 flex flex-wrap items-end gap-2 rounded-md border border-border bg-muted p-3">
      {/* Epic 2 — a 'lot'/'team' cohort can span multiple classes too (same
          ambiguity as 'all'), so it needs this picker just like 'all' does;
          only 'class' already knows its one class (see the `useState`
          initializer above). */}
      {cohort.scope !== 'class' && (
        <label className="flex flex-col gap-1 text-xs text-muted-foreground">
          Asset class
          <select
            value={pickedClassId ?? ''}
            onChange={(e) => {
              setPickedClassId(e.target.value || null);
              setSelectedAssetId('');
            }}
            className="rounded-md border border-border px-2 py-1.5 text-sm text-foreground"
          >
            <option value="">Select…</option>
            {/* Owned classes only. This picker feeds the owned-asset list
                immediately to its right, so a taxonomy-wide list was the most
                visible version of the bug: pick a class you own nothing in,
                get an empty "Asset" select and no explanation. */}
            {assetClasses.map((ac) => (
              <option key={ac.id} value={ac.id}>
                {classOptionLabel(ac)}
              </option>
            ))}
          </select>
        </label>
      )}
      <label className="flex flex-col gap-1 text-xs text-muted-foreground">
        Asset
        <select
          value={selectedAssetId}
          onChange={(e) => setSelectedAssetId(e.target.value)}
          disabled={!pickedClassId || assetsQ.isLoading}
          className="min-w-[200px] rounded-md border border-border px-2 py-1.5 text-sm text-foreground disabled:bg-muted disabled:text-muted-foreground"
        >
          <option value="">{assetsQ.isLoading ? 'Loading…' : 'Select an asset…'}</option>
          {(assetsQ.data?.assets ?? []).map((a) => (
            <option key={a.assetId} value={a.assetId}>
              {a.serialNumber ?? a.assetId.slice(0, 8)}
            </option>
          ))}
        </select>
      </label>
      <Button size="sm"
        disabled={!selectedAssetId || draftMutation.isPending}
        onClick={() => draftMutation.mutate()}
      >
        {draftMutation.isPending ? 'Creating…' : 'Create draft listing'}
      </Button>
      <button
        type="button"
        onClick={() => setOpen(false)}
        className="text-xs text-muted-foreground hover:text-foreground"
      >
        Cancel
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Stage 1, tab "Replacements" — refresh candidate picker (only refresh cohorts)
// ---------------------------------------------------------------------------

function RefreshStep({
  cohorts,
  allCohorts,
  assetClasses,
  onChange,
}: {
  cohorts: CohortDraft[];
  allCohorts: CohortDraft[];
  assetClasses: AssetClassRef[];
  onChange: (cohorts: CohortDraft[]) => void;
}) {
  if (cohorts.length === 0) {
    return (
      <div className="rounded-md border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
        No refresh cohorts yet — set a cohort's action to "Refresh" on the Lifecycle paths tab
        to pick a replacement SKU here.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {cohorts.map((c) => {
        const className = assetClasses.find((ac) => ac.id === c.assetClassId)?.name ?? c.assetClassId!;
        return (
          <div key={c.key} className="space-y-2">
            <div className="text-sm font-medium text-foreground">
              {c.label} <span className="font-normal text-muted-foreground">— {className}</span>
            </div>
            <RefreshCandidateTable
              assetClassId={c.assetClassId!}
              assetClassName={className}
              selectedSkuId={c.refreshSkuId}
              onSelect={(skuId, consolidationRatio) =>
                onChange(
                  allCohorts.map((x) =>
                    x.key === c.key ? { ...x, refreshSkuId: skuId, consolidationRatio } : x,
                  ),
                )
              }
            />
          </div>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Stage 1, tab "Purchases" (SIL-571, I2-C4) — buy cohorts. Unlike Replacements
// (which retires an owned fleet), a buy cohort owns nothing yet — its
// `selector`/`assetClassId` carry no economic meaning, only a client-side
// filter for the SKU picker below (reused from Replacements' candidate table
// — same class-filtered catalog, scoped to classes the org already owns
// something in; a known v1 limitation, not a hard requirement of the SKU
// catalog itself). Price/provenance (`buyUnitPriceUsd`/`buyPriceSuppressed`)
// are READ-ONLY here — always server-computed at save time from the
// street-band median (spec §3.4), never editable in this form.
// ---------------------------------------------------------------------------

function PurchasesStep({
  cohorts,
  allCohorts,
  assetClasses,
  onChange,
}: {
  cohorts: CohortDraft[];
  allCohorts: CohortDraft[];
  assetClasses: AssetClassRef[];
  onChange: (cohorts: CohortDraft[]) => void;
}) {
  if (cohorts.length === 0) {
    return (
      <div className="rounded-md border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
        No purchase cohorts yet — set a cohort's action to "Buy" on the Lifecycle paths tab to
        price a purchase here.
      </div>
    );
  }

  const update = (key: string, patch: Partial<CohortDraft>) =>
    onChange(allCohorts.map((c) => (c.key === key ? { ...c, ...patch } : c)));

  return (
    <div className="space-y-6">
      {cohorts.map((c) => {
        const className = assetClasses.find((ac) => ac.id === c.assetClassId)?.name ?? null;
        return (
          <div key={c.key} className="space-y-2 rounded-lg border border-border bg-card p-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="text-sm font-medium text-foreground">
                {c.label}
                {className && <span className="font-normal text-muted-foreground"> — {className}</span>}
              </div>
              <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
                Units
                <input
                  type="number"
                  min={1}
                  step={1}
                  value={c.consolidationRatio ?? 1}
                  onChange={(e) =>
                    update(c.key, { consolidationRatio: Math.max(1, Math.round(Number(e.target.value) || 1)) })
                  }
                  className="w-16 rounded-md border border-border px-2 py-1 text-right text-sm"
                  aria-label={`Units to buy for ${c.label}`}
                />
              </label>
            </div>

            {!c.assetClassId ? (
              <label className="flex flex-col gap-1 text-xs text-muted-foreground">
                Asset class (filters the SKU catalog below)
                <select
                  value=""
                  onChange={(e) => update(c.key, { scope: 'class', assetClassId: e.target.value || null })}
                  className="w-fit rounded-md border border-border px-2 py-1.5 text-sm text-foreground"
                >
                  <option value="">Select…</option>
                  {assetClasses.map((ac) => (
                    <option key={ac.id} value={ac.id}>
                      {classOptionLabel(ac)}
                    </option>
                  ))}
                </select>
              </label>
            ) : (
              <RefreshCandidateTable
                assetClassId={c.assetClassId}
                assetClassName={className ?? c.assetClassId}
                selectedSkuId={c.buySkuId}
                onSelect={(skuId) => update(c.key, { buySkuId: skuId })}
              />
            )}

            {/* Provenance tier — same disclosure discipline as every other
                assumption (spec §3.4): shown identically whether priced or
                suppressed, never a silent absence. Read-only, resolved
                server-side on save. */}
            {c.id == null ? (
              <p className="text-xs text-muted-foreground">Save the scenario to price this purchase.</p>
            ) : c.buyPriceSuppressed ? (
              <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-2.5 py-2 text-xs text-amber-800">
                <AlertTriangle className="mt-0.5 size-3.5 shrink-0 text-amber-600" />
                <span>
                  Can&apos;t price this purchase — insufficient market depth. Excluded from this
                  scenario&apos;s economics and blocks approval until it prices or is removed.
                </span>
              </div>
            ) : c.buyUnitPriceUsd != null ? (
              <div className="flex items-center gap-2 text-xs text-foreground">
                <StatusPill tone="green">Priced</StatusPill>
                <span>
                  {formatUsd(c.buyUnitPriceUsd, { compact: true })}/unit · street-band median
                  {c.consolidationRatio ? ` · ${formatUsd(c.buyUnitPriceUsd * c.consolidationRatio, { compact: true })} total` : ''}
                </span>
              </div>
            ) : (
              <p className="text-xs text-muted-foreground">
                Pick a SKU above, then save to resolve its street-band price.
              </p>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Stage 2 — Review assumptions: one table, rows == SENSITIVITY_ATTRIBUTES +
// discount rate, in that order (SIL-547 issue). Provenance is a TIER
// (Measured / Priced / Declared / Platform default / Set for this scenario),
// never a freshness verdict — staleness is a badge on the tiers that carry a
// date, and `suppressed` (below the k-anonymity floor) is a third state, not
// an absence.
// ---------------------------------------------------------------------------

const FMV_BANDS: { value: FmvBand; label: string }[] = [
  { value: 'low', label: 'Low' },
  { value: 'med', label: 'Median' },
  { value: 'high', label: 'High' },
];

type Tier = 'measured' | 'priced' | 'declared' | 'platform_default' | 'set_for_scenario' | 'suppressed' | 'n/a';

const TIER_LABEL: Record<Tier, string> = {
  measured: 'Measured',
  priced: 'Priced',
  declared: 'Declared',
  platform_default: 'Platform default',
  set_for_scenario: 'Set for this scenario',
  suppressed: 'Suppressed',
  'n/a': 'Not applicable',
};

const TIER_TONE: Record<Tier, PillTone> = {
  measured: 'green',
  priced: 'green',
  declared: 'green',
  platform_default: 'neutral',
  set_for_scenario: 'neutral',
  suppressed: 'amber',
  'n/a': 'neutral',
};

function TierBadge({ tier, title }: { tier: Tier; title?: string }) {
  return (
    <StatusPill tone={TIER_TONE[tier]} title={title}>
      {TIER_LABEL[tier]}
    </StatusPill>
  );
}

function AssumptionRow({
  label,
  value,
  tier,
  tierTitle,
  note,
  children,
  stale,
  staleTitle,
}: {
  label: string;
  /** The current value, rendered as read text — the editable control (if
   * any) is `children`, kept separate so the value/tier/note columns line
   * up identically for rows that have no editable control at all
   * (`refresh_street_price`). */
  value: ReactNode;
  tier: Tier;
  tierTitle?: string;
  note?: ReactNode;
  children?: ReactNode;
  /** SIL-532 (I2-C2) — a SECOND badge, never folded into `tier`: tier is
   * provenance (never a freshness verdict, per this table's own header
   * note), staleness is a badge on the tiers that carry a date. `undefined`
   * when this row has nothing dated to compare (e.g. the scenario is
   * unsaved, or this row's source resolved no dated assumption). */
  stale?: boolean;
  staleTitle?: string;
}) {
  return (
    <div className="grid grid-cols-1 gap-2 border-t border-border py-3 sm:grid-cols-[1fr_auto_auto] sm:items-center sm:gap-4">
      <div>
        <p className="text-sm font-medium text-foreground">{label}</p>
        {note && <p className="mt-0.5 text-xs text-muted-foreground">{note}</p>}
      </div>
      <div className="text-sm text-foreground sm:text-right">{children ?? value}</div>
      <div className="flex items-center justify-end gap-1.5 sm:justify-self-end">
        {stale && (
          <StatusPill tone="amber" title={staleTitle}>
            Stale
          </StatusPill>
        )}
        <TierBadge tier={tier} title={tierTitle} />
      </div>
    </div>
  );
}

function AssumptionTable({
  draft,
  onChange,
  scenarioId,
  discountRatePct,
  onChangeDiscountRatePct,
  staleness,
}: {
  draft: ScenarioDraft;
  onChange: (patch: Partial<ScenarioDraft>) => void;
  scenarioId: string | null;
  discountRatePct: number | null;
  onChangeDiscountRatePct: (v: number | null) => void;
  /** SIL-532 (I2-C2) — the SAME read model backing the Overview counter and
   * the scenario-list badges (`GET /scenarios/:id`'s `staleness` field).
   * `undefined` for an unsaved draft — nothing has been priced against yet. */
  staleness?: ScenarioStalenessDto;
}) {
  const sensitivityQ = useScenarioSensitivityQuery(scenarioId);
  const meQ = useMeQuery();
  const attrs = sensitivityQ.data?.attributes ?? [];
  const byId = (id: string) => attrs.find((a) => a.id === id);
  const staleAssumption = (id: string) => staleness?.assumptions.find((a) => a.id === id);
  const staleTitleFor = (id: string) => {
    const a = staleAssumption(id);
    return a?.stale
      ? `Repriced ${formatDate(a.pricedAt)} — after this scenario was last saved (${formatDate(staleness!.comparedAgainst)}).`
      : undefined;
  };

  const hasRefreshCohort = draft.cohorts.some((c) => c.action === 'refresh');
  const hasItadCohort = draft.cohorts.some((c) => c.action === 'itad');
  const refreshAttr = byId('refresh_street_price');
  const residualAttr = byId('residual_recovery');

  // Priced band (p25/p75) travels on the attribute's own `basis.inputs` —
  // the same [low, high] the API perturbed the engine with, never
  // re-derived here.
  const refreshBand = refreshAttr?.applicable
    ? {
        p25: refreshAttr.basis.inputs.find((i) => i.name === 'lowBound')?.value ?? null,
        p75: refreshAttr.basis.inputs.find((i) => i.name === 'highBound')?.value ?? null,
      }
    : null;
  const refreshSuppressed = !refreshAttr?.applicable && (refreshAttr?.reason ?? '').includes('k-anonymity');

  const orgPue = meQ.data?.pue ?? null;
  const orgPueUpdatedAt = meQ.data?.pueUpdatedAt ?? null;

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Every dollar and carbon figure on Compare & decide is computed from the rows below. Each
        row shows its provenance TIER, not a freshness verdict — a stale-but-real number and a
        missing one are different problems.
      </p>
      {!scenarioId && (
        <p className="text-xs text-muted-foreground">
          Save the scenario to see the priced/declared rows resolve against your cohorts — Refresh
          SKU market price and Residual recovery % both depend on what's saved.
        </p>
      )}

      <div className="rounded-xl border border-border bg-card px-4">
        {/* refresh_street_price */}
        <AssumptionRow
          label={SENSITIVITY_ATTRIBUTES[0]!.label}
          value={
            !hasRefreshCohort
              ? '—'
              : refreshSuppressed
                ? 'Suppressed'
                : refreshBand?.p25 != null && refreshBand.p75 != null
                  ? `${formatUsd(refreshBand.p25, { compact: true })}–${formatUsd(refreshBand.p75, { compact: true })}`
                  : '—'
          }
          tier={!hasRefreshCohort ? 'n/a' : refreshSuppressed ? 'suppressed' : refreshAttr?.applicable ? 'priced' : 'n/a'}
          tierTitle={
            !hasRefreshCohort
              ? 'No refresh cohort — market price has no effect on this scenario.'
              : refreshSuppressed
                ? 'Below the k-anonymity floor — too few comps to publish a bound, not an absence of data.'
                : undefined
          }
          note={
            !hasRefreshCohort
              ? 'Add a refresh cohort on Define paths to price the replacement SKU.'
              : refreshAttr?.reason && !refreshAttr.applicable
                ? refreshAttr.reason
                : 'Market comp band (p25–p75) for the refresh SKU.'
          }
          stale={staleAssumption('refresh_street_price')?.stale}
          staleTitle={staleTitleFor('refresh_street_price')}
        />

        {/* power_kwh */}
        <AssumptionRow
          label={SENSITIVITY_ATTRIBUTES[1]!.label}
          value={null}
          tier={draft.kwhUsd === PLATFORM_DEFAULT_KWH_USD ? 'platform_default' : 'set_for_scenario'}
        >
          <span className="inline-flex items-center gap-1 justify-end">
            <input
              type="number"
              min={0}
              step={0.01}
              value={draft.kwhUsd}
              onChange={(e) => onChange({ kwhUsd: Math.max(0, Number(e.target.value) || 0) })}
              className="w-24 rounded-md border border-border px-2 py-1 text-right text-sm"
              aria-label="Electricity rate, $/kWh"
            />
            <span className="text-xs text-muted-foreground">$/kWh</span>
            <InfoTooltip label="About power cost">
              Power cost = watts × hours-in-month × this rate. When an asset has no measured
              telemetry for the last 30 days, its wattage falls back to 0.50 × the SKU's rated TDP —
              a conservative "typical utilization" estimate, not full nameplate draw.
            </InfoTooltip>
          </span>
        </AssumptionRow>

        {/* fmv_band */}
        <AssumptionRow
          label={SENSITIVITY_ATTRIBUTES[2]!.label}
          value={null}
          tier="priced"
          tierTitle="Priced off the SiliconX market model — the fixed ±15% cone around median FMV."
          stale={staleAssumption('fmv_band')?.stale}
          staleTitle={staleTitleFor('fmv_band')}
        >
          <span className="inline-flex items-center gap-1 justify-end">
            <select
              value={draft.fmvBand}
              onChange={(e) => onChange({ fmvBand: e.target.value as FmvBand })}
              className="rounded-md border border-border px-2 py-1 text-sm"
            >
              {FMV_BANDS.map((b) => (
                <option key={b.value} value={b.value}>
                  {b.label}
                </option>
              ))}
            </select>
            <InfoTooltip label="About the FMV band">
              The Low / Median / High band applies a fixed ±15% cone around the median FMV — the
              same band the FMV Outlook card uses. Ops-internal stored low/high bands are never
              exposed to the client.
            </InfoTooltip>
          </span>
        </AssumptionRow>

        {/* pue */}
        <AssumptionRow
          label={SENSITIVITY_ATTRIBUTES[3]!.label}
          value={null}
          tier={draft.pue != null ? 'set_for_scenario' : orgPue != null ? 'declared' : 'platform_default'}
          tierTitle={
            draft.pue != null
              ? undefined
              : orgPue != null
                ? `Declared for your organization${orgPueUpdatedAt ? ` — ${formatDate(orgPueUpdatedAt)}` : ''}.`
                : 'No org-declared PUE — the published industry default applies at compute time.'
          }
          stale={staleAssumption('pue')?.stale}
          staleTitle={staleTitleFor('pue')}
        >
          <span className="inline-flex items-center gap-1 justify-end">
            <input
              type="number"
              min={1}
              step={0.01}
              value={draft.pue ?? ''}
              placeholder={orgPue != null ? String(orgPue) : '—'}
              onChange={(e) => onChange({ pue: e.target.value === '' ? null : Math.max(1, Number(e.target.value) || 1) })}
              className="w-20 rounded-md border border-border px-2 py-1 text-right text-sm"
              aria-label="PUE"
            />
            <InfoTooltip label="About PUE" align="right">
              Power Usage Effectiveness — data-center overhead multiplier on top of IT-equipment
              power draw. Leave blank to use your organization's declared PUE (Profile → Facilities),
              or the industry default if none is declared.
            </InfoTooltip>
          </span>
        </AssumptionRow>

        {/* residual_recovery */}
        <AssumptionRow
          label={SENSITIVITY_ATTRIBUTES[4]!.label}
          value={null}
          tier={
            !hasItadCohort
              ? 'n/a'
              : draft.itadRecoveryPct === PLATFORM_DEFAULT_ITAD_RECOVERY_PCT
                ? 'platform_default'
                : 'set_for_scenario'
          }
          tierTitle={!hasItadCohort ? residualAttr?.reason ?? 'No ITAD cohort — this has no effect on the scenario.' : undefined}
        >
          <span className="inline-flex items-center gap-1 justify-end">
            <input
              type="number"
              min={0}
              max={100}
              step={1}
              value={draft.itadRecoveryPct}
              onChange={(e) =>
                onChange({ itadRecoveryPct: Math.min(100, Math.max(0, Number(e.target.value) || 0)) })
              }
              className="w-16 rounded-md border border-border px-2 py-1 text-right text-sm"
              aria-label="ITAD recovery %"
            />
            <span className="text-xs text-muted-foreground">%</span>
            <InfoTooltip label="About ITAD recovery %">
              The percentage of current FMV recovered when a cohort is sent to ITAD (certified
              recycling/data-destruction) instead of resold — the baseline default (34%) reflects a
              typical wholesale recycler payout, well below open-market resale.
            </InfoTooltip>
          </span>
        </AssumptionRow>

        {/* discount rate — client-only, no persisted home, no engine term.
            See ScenarioWizardPage's discountRatePct docstring. */}
        <AssumptionRow
          label="Discount rate"
          value={null}
          tier={discountRatePct != null ? 'set_for_scenario' : 'n/a'}
          tierTitle="No platform discount-rate policy exists — this is always a finance judgement you enter, never inherited."
          note="Not yet applied to any figure below — recorded for reference only."
        >
          <span className="inline-flex items-center gap-1 justify-end">
            <input
              type="number"
              min={0}
              max={100}
              step={0.1}
              value={discountRatePct ?? ''}
              placeholder="—"
              onChange={(e) =>
                onChangeDiscountRatePct(e.target.value === '' ? null : Math.max(0, Number(e.target.value) || 0))
              }
              className="w-16 rounded-md border border-border px-2 py-1 text-right text-sm"
              aria-label="Discount rate %"
            />
            <span className="text-xs text-muted-foreground">%</span>
          </span>
        </AssumptionRow>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Stage 3 — Compare & decide
// ---------------------------------------------------------------------------

/** The assumption-table rows this scenario's `sourcedCount` is measured
 * against — SENSITIVITY_ATTRIBUTES plus the client-only discount-rate row.
 * Derived (not a literal `6`) so it can never silently disagree with
 * SENSITIVITY_ATTRIBUTES if that array grows — see
 * `__tests__/assumption-row-count.test.ts` for the pin. */
const ASSUMPTION_ROW_COUNT = SENSITIVITY_ATTRIBUTES.length + 1;

/** A section head for one of Compare & decide's three named bands (Fix 1 —
 * "give the result a spine"). Plain `<h2>`, sized off the caption step so
 * it reads as scaffolding, not a competing headline. */
function ResultBand({ title, children }: { title: string; children: ReactNode }) {
  const headingId = useId();
  return (
    <section aria-labelledby={headingId} className="space-y-4">
      <h2 id={headingId} className="text-2xs font-semibold uppercase tracking-[0.1em] text-muted-foreground">
        {title}
      </h2>
      {children}
    </section>
  );
}

function CompareStep({
  draft,
  // Aliased: the raw prop is the LIVE compute result kept in page-level
  // state. Once the scenario is `approved`, the page cites the frozen
  // approval snapshot instead — never this — see `renderCompareBody` below,
  // which shadows `compareResult` with whichever of the two applies.
  compareResult: liveCompareResult,
  isComputing,
  onCompute,
  shareUrl,
  scenarioId,
  assetClasses,
  lots,
  teams,
  carbonBuys,
  carbonMoves,
  onChangeCarbonBuys,
  onChangeCarbonMoves,
  showCarbonLensEditor,
  onToggleCarbonLensEditor,
  discountRatePct,
  isDirty,
  canAutosave,
  scenarioRow,
}: {
  draft: ScenarioDraft;
  compareResult: ScenarioCompareResponse | null;
  isComputing: boolean;
  onCompute: () => void;
  shareUrl: string | null;
  scenarioId: string | null;
  assetClasses: AssetClassRef[];
  /** Epic 2 — passed through to the carbon lens's Move section (see
   * ScenarioBuyMoveStep.tsx's own `lots`/`teams` props). */
  lots: ScenarioLotOption[];
  teams: ScenarioTeamOption[];
  carbonBuys: CarbonBuyDraft[];
  carbonMoves: CarbonMoveDraft[];
  onChangeCarbonBuys: (next: CarbonBuyDraft[]) => void;
  onChangeCarbonMoves: (next: CarbonMoveDraft[]) => void;
  showCarbonLensEditor: boolean;
  onToggleCarbonLensEditor: () => void;
  discountRatePct: number | null;
  isDirty: boolean;
  canAutosave: boolean;
  scenarioRow: FinancialScenarioRow | undefined;
}) {
  const [copied, setCopied] = useState(false);
  const [showEvidenceSankeys, setShowEvidenceSankeys] = useState(false);
  const [showCaveatsCarbon, setShowCaveatsCarbon] = useState(false);

  // "Link copied" reverts on its own after 1.5s — cleared on unmount so a
  // late setState never fires (React warns) if the reader navigates away
  // mid-timeout.
  useEffect(() => {
    if (!copied) return;
    const t = setTimeout(() => setCopied(false), 1500);
    return () => clearTimeout(t);
  }, [copied]);

  // SIL — scenario freeze-on-approval follow-up. `enabled` on `isApproved`
  // alone (not on any compare-result state) per `useScenarioApprovalSnapshotQuery`'s
  // own doc comment; `renderCompareBody` below is the ONE place this feeds a
  // rendering decision.
  const isApprovedScenario = scenarioRow?.status === 'approved';
  const approvalSnapshotQuery = useScenarioApprovalSnapshotQuery(scenarioId, isApprovedScenario);

  /**
   * SIL-440 — carbon targets for the forecast card's target line (spec §4:
   * "chart draws the target matching the active filter scope"). Fetched only
   * once a comparison exists, because that is the only state in which the
   * chart that consumes it renders. A failed/absent fetch draws NO line
   * rather than a zero one — an org with no target set and an org whose
   * target failed to load must not both render as "target: 0". Deliberately
   * NOT wrapped in `<QueryBoundary>`: that would turn a documented graceful
   * degrade ("no line drawn if failed") into "hide the whole forecast card
   * on a network hiccup", which is a worse failure mode than the one being
   * fixed elsewhere on this page — see the PR description. `isApprovedScenario`
   * is ORed in because the frozen/unavailable branch is only known once
   * `approvalSnapshotQuery` resolves, below — this hook can't wait on that.
   */
  const targetsQuery = useQuery<CarbonTargetsResponse>({
    queryKey: qk.sustainability.targets(),
    queryFn: async () => unwrap<CarbonTargetsResponse>(await api.api.client.sustainability.targets.$get()),
    enabled: !!liveCompareResult || isApprovedScenario,
  });
  const carbonTargets = useMemo(() => {
    const data = targetsQuery.data;
    if (!data) return [];
    return data.org ? [data.org, ...data.teams] : data.teams;
  }, [targetsQuery.data]);

  const sensitivityQ = useScenarioSensitivityQuery(scenarioId);
  const attrs = sensitivityQ.data?.attributes ?? [];
  const byId = (id: string) => attrs.find((a) => a.id === id);

  /**
   * "N of 6 sourced" — a COUNT, never a grade (issue: "no graded
   * confidence"). Sourced means the row has a real, attributable source
   * behind its current value, not merely a rendered number:
   *   - refresh_street_price / residual_recovery: applicable per the
   *     sensitivity endpoint (i.e. a matching cohort exists AND the API
   *     could resolve a bound for it).
   *   - power_kwh / fmv_band / pue: always sourced — every scenario has a
   *     rate (platform default or override), a band, and a resolved PUE
   *     (org-declared or industry default).
   *   - discount rate: sourced only once the reader has actually entered
   *     one — it has no fallback tier.
   *
   * `sourcedRows` names the six rows for the reader (previously only a code
   * comment knew what "of 6" counted) — `NetPositionHero`'s tooltip lists
   * them verbatim so "3 of 6 sourced" is defensible, not asserted.
   */
  const sourcedRows = useMemo(() => {
    const refreshSourced = byId('refresh_street_price')?.applicable ?? false;
    const residualSourced = byId('residual_recovery')?.applicable ?? false;
    return [
      { label: SENSITIVITY_ATTRIBUTES[0]!.label, sourced: refreshSourced },
      { label: SENSITIVITY_ATTRIBUTES[1]!.label, sourced: true },
      { label: SENSITIVITY_ATTRIBUTES[2]!.label, sourced: true },
      { label: SENSITIVITY_ATTRIBUTES[3]!.label, sourced: true },
      { label: SENSITIVITY_ATTRIBUTES[4]!.label, sourced: residualSourced },
      { label: 'Discount rate', sourced: discountRatePct != null },
    ];
  }, [attrs, discountRatePct]);
  const sourcedCount = sourcedRows.filter((r) => r.sourced).length;

  const hasBuySummary =
    carbonBuys.some(isBuyDraftComplete) || carbonMoves.some(isMoveDraftComplete);

  const ownedCohorts = useMemo(
    () =>
      draft.cohorts.map((c) => ({
        key: c.key,
        label: c.label,
        assetClassId: c.scope === 'class' ? c.assetClassId : null,
      })),
    [draft.cohorts],
  );

  // Chart event markers — additive (no ReferenceLine existed before). One
  // per distinct cohort action month; `hold` is excluded (its timing is
  // inert, same rule `isStranded` uses).
  const eventMonths = useMemo(
    () =>
      draft.cohorts
        .filter((c) => c.action !== 'hold')
        .map((c) => ({
          month: c.timingMonths,
          label: `${c.label} — ${ACTIONS.find((a) => a.value === c.action)?.label ?? c.action}`,
        })),
    [draft.cohorts],
  );

  /**
   * The ONE place "which figures does this render" is decided. `view.kind`:
   *   - `'live'` — unchanged pre-SIL behavior; `compareResult` is the
   *     page-level live-compute state.
   *   - `'frozen'` — the scenario is approved and a snapshot exists.
   *     EVERY figure below (hero, capex tile, metric table, chart,
   *     Sankeys, carbon block, excluded-cohort/lot-team disclosures, the
   *     assumption chips) is sourced from `frozen.compareResult`/`frozen`
   *     itself — never `liveCompareResult`, even if a stray recompute
   *     populated it. That's what makes the capex tile and `ApprovalBar`'s
   *     `approvedCapexUsd` provably unable to disagree: both ultimately
   *     trace to the SAME snapshot row (`approvedCapexUsd` on
   *     `financial_scenario` and `compareResult.metrics.delta.refreshCapexUsd`
   *     on the snapshot were written from the same in-memory value in the
   *     same transaction — see `POST /:id/approve`).
   *   - `'unavailable'` — approved pre-748, no frozen row. `compareResult`
   *     is forced to `null` here (NOT `liveCompareResult`) so a stray
   *     recompute can never silently stand in for the missing snapshot —
   *     the exact bug this feature closes one level down.
   */
  function renderCompareBody(view: ApprovedCompareView) {
    const frozen = view.kind === 'frozen' ? view.snapshot : null;
    const isUnavailable = view.kind === 'unavailable';
    const compareResult = resolveEffectiveCompareResult(view, liveCompareResult);
    const hasCarbonAdvisory = !!compareResult?.carbonAdvisory;
    // Assumption chips — frozen alongside the dollars they produced when a
    // snapshot is cited (SIL: chips MUST freeze in lockstep with the
    // figures, or the page shows frozen dollars beside live/drifted
    // assumptions, the same bug one level down). `draft`'s live values
    // otherwise, matching pre-existing behavior.
    const {
      horizonMonths: chipHorizonMonths,
      fmvBand: chipFmvBand,
      kwhUsd: chipKwhUsd,
      pue: chipPue,
      itadRecoveryPct: chipItadRecoveryPct,
    } = resolveChipAssumptions(view, draft);

    return (
      <>
        {compareResult && scenarioId && (
          <div className="flex justify-end print:hidden">
            <ExportButtonGroup
              csvPath={`/api/client/scenarios/${scenarioId}/export/compare.csv`}
              csvFilename="scenario-compare.csv"
            />
          </div>
        )}

        {frozen && <ApprovedSnapshotCitation snapshot={frozen} />}

        <div className="flex flex-wrap items-center gap-2 print:hidden">
          <AssumptionChip label={`${chipHorizonMonths}mo horizon`} />
          <AssumptionChip label={`${chipFmvBand.toUpperCase()} FMV band`}>
            Fixed ±15% cone around the median FMV.
          </AssumptionChip>
          <AssumptionChip label={`$${chipKwhUsd.toFixed(2)}/kWh`}>
            Power cost basis. Assets without 30-day telemetry fall back to 0.50 × rated TDP.
          </AssumptionChip>
          {chipPue != null && <AssumptionChip label={`PUE ${chipPue.toFixed(2)}`} />}
          <AssumptionChip label={`ITAD ${chipItadRecoveryPct}% recovery`}>
            Percentage of current FMV recovered when a cohort is sent to ITAD instead of resold.
          </AssumptionChip>
          {shareUrl && (
            <button
              type="button"
              onClick={() => {
                void navigator.clipboard.writeText(shareUrl);
                setCopied(true);
              }}
              className="ml-auto rounded-md border border-border px-2.5 py-1 text-xs text-muted-foreground hover:bg-muted"
            >
              {copied ? 'Link copied' : 'Copy shareable link'}
            </button>
          )}
        </div>

        {/* Approved before migration 748 shipped — deliberately NOT
            backfilled (see the DTO's doc comment). This must never fall
            back to a live recompute; the notice is the whole page has to
            offer for this scenario. */}
        {isUnavailable && <ApprovalSnapshotUnavailableNotice />}

        {!compareResult && !isUnavailable && (
          <div className="flex flex-col items-center gap-3 rounded-xl border border-dashed border-border py-14 text-center">
            <div className="text-sm text-muted-foreground">
              {/* The duplicate "Compute comparison" button that used to sit
                  here is gone — auto-compute (on entering this stage) and the
                  footer's primary button already cover every entry path; a
                  third button at a different scroll position was a no-op the
                  reader couldn't distinguish from a needed action. */}
              {isComputing
                ? 'Computing the comparison…'
                : "Save your cohorts and assumptions, then use Compute comparison below."}
            </div>
          </div>
        )}

        {compareResult && (
          <>
          {/* BAND 1 — the answer. Hero directly followed by the metric
              table (Fix 1): the reader's job on this screen is a dollar
              decision, so the number and its breakdown sit first, not two
              screens under 400px of Sankeys. */}
          <ResultBand title="The answer">
            {/* SIL-1828 (R2): server flag; label only, no component detail. */}
            {compareResult.includesDirectionalBaseline && (
              <div
                data-testid="scenario-directional-label"
                className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800"
              >
                <AlertTriangle className="mt-0.5 size-3.5 shrink-0 text-amber-600" />
                <span>
                  Directional: some assets start from a {DIRECTIONAL_BASELINE_LABEL.toLowerCase()} because no market
                  comps exist yet for their hardware. Treat the figures below as directional, not exact.
                </span>
              </div>
            )}
            <NetPositionHero
              metrics={compareResult.metrics}
              basis={compareResult.basis}
              generatedAt={compareResult.generatedAt}
              sourcedCount={sourcedCount}
              totalRows={ASSUMPTION_ROW_COUNT}
              sourcedRows={sourcedRows}
            />
            <div className="rounded-xl border border-border bg-card p-5">
              <SectionHead
                trigger={
                  <InfoTooltip label="About upfront cash required">
                    Refresh + buy capex only — the cost of replacement SKUs and priced purchase
                    cohorts this scenario models. A suppressed (unpriceable) buy cohort is excluded
                    here, not counted as $0 — see its{' '}
                    {scenarioId && (
                      <Link
                        to="/financials/scenarios/$scenarioId"
                        params={{ scenarioId }}
                        search={{ stage: 1, tab: 'purchases' }}
                        className="underline"
                      >
                        disclosure on the Purchases tab
                      </Link>
                    )}
                    . Hypothetical purchases/relocations modeled in the carbon lens below carry no
                    price field and are not included.
                  </InfoTooltip>
                }
              >
                Upfront cash required
              </SectionHead>
              <div className="mt-1 font-num text-2xl font-semibold text-foreground">
                {formatUsd(compareResult.metrics.delta.refreshCapexUsd)}
              </div>
              <div className="mt-1 text-xs text-muted-foreground">Capex only.</div>
            </div>
            <MetricTable metrics={compareResult.metrics} />
          </ResultBand>

          {/* BAND 2 — the evidence. Compare chart, then sensitivity, then
              the time-axis Sankeys collapsed by default (Fix 1) — reusing
              the exact disclosure idiom proven on the carbon lens, not a
              second collapsible pattern. */}
          <ResultBand title="The evidence">
            <div className="rounded-xl border border-border bg-card p-5">
              <SectionHead
                trigger={
                  <CalculationBasisTrigger
                    title="Net position — baseline vs scenario"
                    basis={compareResult.basis}
                  />
                }
              >
                Your scenario vs. holding
              </SectionHead>
              <ScenarioCompareChart series={compareResult.series} eventMonths={eventMonths} />
            </div>

            <SensitivityPanel scenarioId={scenarioId} />

            <CollapsibleCard
              title="Time-axis detail — value & carbon flow"
              subtitle="Cohort-by-cohort Sankeys through the horizon. Collapsed by default — the table above is the decision surface."
              open={showEvidenceSankeys}
              onToggle={() => setShowEvidenceSankeys((s) => !s)}
            >
              <ScenarioTimeSankeys compareResult={compareResult} />
            </CollapsibleCard>
          </ResultBand>

          {/* BAND 3 — the caveats & carbon. Excluded cohorts and lot/team
              membership drift are named directly; every carbon surface
              (forecast, lens editor, advisory, buy summary, tornado) is
              consolidated behind ONE disclosure, matching the fact this
              engine already treats carbon as a request-scoped lens — not
              part of the dollar series, not saved with the scenario. */}
          <ResultBand title="The caveats & carbon">
            {/* SIL-571 — named exclusion for every suppressed buy cohort, so
                a reader never mistakes "excluded from the numbers below" for
                "priced at $0" (spec §3.4's disclosed-fallback discipline). */}
            {compareResult.excludedCohorts && compareResult.excludedCohorts.length > 0 && (
              <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                <AlertTriangle className="mt-0.5 size-3.5 shrink-0 text-amber-600" />
                <span>
                  {compareResult.excludedCohorts.length === 1
                    ? '1 cohort is'
                    : `${compareResult.excludedCohorts.length} cohorts are`}{' '}
                  excluded from the figures above:{' '}
                  {compareResult.excludedCohorts.map((ec) => `"${ec.label}" (${ec.reason})`).join('; ')}.
                </span>
              </div>
            )}

            {/* Epic 2 (plans/lots-first-class.md) — lot/team cohort
                membership is resolved LIVE at compute time and nothing
                about it is persisted, so the numbers above can silently
                drift from what a reader saw last time the same scenario
                was opened — UNLESS a frozen snapshot is being cited
                (`frozen` below), in which case this specific count is
                exactly as immutable as the rest of the figures on this
                page. */}
            <LotTeamCohortMembership draft={draft} compareResult={compareResult} frozen={!!frozen} />

            <CollapsibleCard
              title="Carbon lens — footprint, purchases & relocations"
              subtitle="Not saved with this scenario, and not in the dollar series above — request-scoped only."
              open={showCaveatsCarbon}
              onToggle={() => setShowCaveatsCarbon((s) => !s)}
            >
              {/* SIL-440 row 1 — forecast (spec §6 Variant A). */}
              <ScenarioCarbonForecast
                useCarbon={compareResult.useCarbon}
                horizonMonths={compareResult.horizonMonths}
                carbon={compareResult.carbon}
                carbonTargets={carbonTargets}
                deltaNetPositionUsd={compareResult.metrics.delta.netPositionUsd}
                scenarioId={scenarioId}
              />

              {/* SIL-547 — carbon lens editor. `buy`/`move` are
                  request-scoped, carbon-only inputs that ride only on this
                  compute, never saved with the scenario. Nested disclosure
                  (same idiom, one level in) so the editor form isn't forced
                  open every time the reader opens the carbon lens itself. */}
              <div className="rounded-lg border border-border bg-muted/30 p-4">
                <button
                  type="button"
                  onClick={onToggleCarbonLensEditor}
                  className="flex w-full items-center justify-between gap-2 text-left"
                  aria-expanded={showCarbonLensEditor}
                >
                  <span className="text-sm font-medium text-foreground">Model a purchase or relocation</span>
                  {showCarbonLensEditor ? (
                    <ChevronUp className="size-4 shrink-0 text-muted-foreground" />
                  ) : (
                    <ChevronDown className="size-4 shrink-0 text-muted-foreground" />
                  )}
                </button>
                {showCarbonLensEditor && (
                  <div className="mt-4 border-t border-border pt-4">
                    <ScenarioBuyMoveStep
                      buys={carbonBuys}
                      moves={carbonMoves}
                      ownedCohorts={ownedCohorts}
                      assetClasses={assetClasses}
                      lots={lots}
                      teams={teams}
                      horizonMonths={draft.horizonMonths}
                      onChangeBuys={onChangeCarbonBuys}
                      onChangeMoves={onChangeCarbonMoves}
                    />
                    <div className="mt-3 flex justify-end">
                      <Button size="sm" onClick={onCompute} disabled={isComputing}>
                        {isComputing ? 'Recomputing…' : 'Recompute with carbon lens'}
                      </Button>
                    </div>
                  </div>
                )}
              </div>

              {/* SIL-440 row 2 — advisory beside the buy-cohort summary
                  echo. Grid only goes two-up when BOTH sides have content —
                  a lone advisory card no longer floats half-width beside
                  dead space when the buy summary self-suppresses. */}
              <div className={cn('grid items-start gap-6', hasCarbonAdvisory && hasBuySummary ? 'lg:grid-cols-2' : 'grid-cols-1')}>
                <CarbonAdvisoryModule advisory={compareResult.carbonAdvisory} />
                <BuyCohortSummaryModule buys={carbonBuys} moves={carbonMoves} />
              </div>

              {/* SIL-443 row 3 — the ship-last carbon tornado (spec §11). */}
              <CarbonSensitivityTornado block={compareResult.carbonSensitivity} scenarioId={scenarioId} />
            </CollapsibleCard>
          </ResultBand>
          </>
        )}
      </>
    );
  }

  return (
    <div className="space-y-6 print:space-y-4">
      {isApprovedScenario ? (
        // The QueryBoundary Adoption Contract's documented trap: this query
        // is `enabled` only while `isApprovedScenario` — without this outer
        // gate, a draft/in-review scenario (where the query never runs)
        // would render QueryBoundary's `isPending` loading skeleton forever
        // instead of the live-compute view below.
        <QueryBoundary query={approvalSnapshotQuery} errorTitle="Couldn't load the approved snapshot.">
          {(snapshotResponse) => renderCompareBody(resolveApprovedCompareView(scenarioRow?.status, snapshotResponse))}
        </QueryBoundary>
      ) : (
        renderCompareBody({ kind: 'live' })
      )}

      <ReadinessChecklist
        draft={draft}
        scenarioId={scenarioId}
        isDirty={isDirty}
        canAutosave={canAutosave}
        scenarioRow={scenarioRow}
      />
    </div>
  );
}

/**
 * The calm, unmistakable "you are reading a frozen record" status — same
 * idiom as `ApprovalBar`'s own locked-and-approved notice (border +
 * bg-muted, series-green Check, no brand emerald fill — a static status
 * doesn't earn that per the One Voice Rule), placed directly beside the
 * figures it describes instead of only at the top of the page.
 */
export function ApprovedSnapshotCitation({ snapshot }: { snapshot: FinancialScenarioApprovalSnapshot }) {
  return (
    <div className="flex items-start gap-2 rounded-md border border-border bg-muted px-2.5 py-2 text-xs text-foreground print:hidden">
      <Check className="mt-0.5 size-3.5 shrink-0" style={{ color: SX_PALETTE[3] }} />
      <span>
        You&rsquo;re looking at the approved figures, frozen as of {formatTs(snapshot.generatedAt)}. They
        won&rsquo;t change on a later visit or if market data shifts. Editing this scenario voids the
        approval and returns you to a live comparison.
      </span>
    </div>
  );
}

/**
 * "No frozen record for this approval" — the scenario IS `approved` but no
 * snapshot row exists for it (approved before migration 748 shipped, and
 * deliberately not backfilled — see `FinancialScenarioApprovalSnapshotResponse`'s
 * doc comment in `dto/financials.ts`). This is the whole point of the
 * `unavailable` branch: state plainly that nothing immutable exists to cite
 * and what to do about it, never a silent live recompute standing in for it.
 */
export function ApprovalSnapshotUnavailableNotice() {
  return (
    <div className="rounded-xl border border-border bg-card p-5 text-sm text-foreground">
      <div className="flex items-start gap-2">
        <AlertTriangle className="mt-0.5 size-4 shrink-0 text-amber-600" />
        <div>
          <p className="font-medium text-foreground">No frozen record for this approval.</p>
          <p className="mt-1 text-muted-foreground">
            This scenario was approved before frozen approval records existed, so there&rsquo;s nothing
            immutable to cite here. Withdraw the approval and approve it again to create one.
          </p>
        </div>
      </div>
    </div>
  );
}

/**
 * SIL team-ownership — displays which TEAM owns this scenario (778/776),
 * distinct from the cohort "By team" scope selector on the Lifecycle paths
 * tab (that picks which team's ASSETS a cohort selects; this is who owns
 * the SCENARIO itself for write access — deliberately its own section with
 * its own header so the two never read as the same control). Org admins get
 * an editable dropdown that calls `client_assign_scenario_team` (via
 * `PUT /api/client/teams/scenario/:id`); everyone else gets a plain
 * read-only label — reassignment is admin-only both here and at the DB.
 */
function ScenarioTeamOwnership({
  scenarioId,
  teamId,
  teamName,
  teams,
  isAdmin,
}: {
  scenarioId: string;
  teamId: string | null;
  teamName: string | null;
  teams: ScenarioTeamOption[];
  isAdmin: boolean;
}) {
  const qc = useQueryClient();

  const assignMutation = useMutation({
    mutationFn: async (newTeamId: string | null) => {
      const res = await api.api.client.teams.scenario[':scenarioId'].$put({
        param: { scenarioId },
        json: { teamId: newTeamId },
      });
      await throwIfNotOk(res);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.scenarios.detail(scenarioId) });
      void qc.invalidateQueries({ queryKey: qk.scenarios.all });
      toast.success('Owning team updated.');
    },
    onError: (err: Error & { body?: { error?: string } }) => {
      toast.error(err.body?.error ?? err.message ?? 'Could not update the owning team.');
    },
  });

  return (
    <div className="rounded-xl border border-border bg-card p-4">
      <SectionLabel>Owning team</SectionLabel>
      <p className="mt-1 text-xs text-muted-foreground">
        The team whose members can edit or delete this scenario. Org admins can always edit any scenario.
      </p>
      {isAdmin ? (
        <select
          value={teamId ?? ''}
          disabled={assignMutation.isPending}
          onChange={(e) => assignMutation.mutate(e.target.value || null)}
          className="mt-2 rounded-md border border-border bg-transparent px-2 py-1 text-sm text-foreground"
          aria-label="Owning team"
        >
          <option value="">Unassigned</option>
          {teams.map((t) => (
            <option key={t.id} value={t.id}>
              {t.name}
            </option>
          ))}
        </select>
      ) : (
        <p className="mt-2 text-sm font-medium text-foreground">{teamName ?? 'Unassigned'}</p>
      )}
    </div>
  );
}

/**
 * SIL-568 — status pill + transition actions (draft → in_review → approved),
 * the locked-scenario read-only notice, and the voided-approval banner. Live
 * `row` fields only (approvedBy/At/CapexUsd/ThresholdUsd, mig 728) — never a
 * client-invented figure. `status` itself is never editable here; the three
 * mutations passed in are the ONLY way it changes (spec §3.2).
 */
function ApprovalBar({
  row,
  isAdmin,
  voidedBanner,
  onDismissVoided,
  onSubmit,
  onApprove,
  onUnapprove,
  isSubmitting,
  isApproving,
  isUnapproving,
}: {
  row: FinancialScenarioRow | undefined;
  isAdmin: boolean;
  voidedBanner: { approvedAt: string } | null;
  onDismissVoided: () => void;
  onSubmit: () => void;
  onApprove: () => void;
  onUnapprove: () => void;
  isSubmitting: boolean;
  isApproving: boolean;
  isUnapproving: boolean;
}) {
  if (!row) return null;
  const status = row.status;

  const tone: PillTone = status === 'approved' ? 'green' : status === 'in_review' ? 'amber' : 'neutral';
  const label = status === 'approved' ? 'Approved' : status === 'in_review' ? 'In review' : 'Draft';

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-3 rounded-xl border border-border bg-card px-4 py-3">
        <StatusPill tone={tone}>{label}</StatusPill>
        {status === 'draft' && (
          <>
            <span className="text-xs text-muted-foreground">Ready for an admin to review?</span>
            <Button size="sm" variant="secondary" onClick={onSubmit} disabled={isSubmitting}>
              {isSubmitting ? 'Submitting…' : 'Submit for approval'}
            </Button>
          </>
        )}
        {status === 'in_review' && (
          <>
            <span className="text-xs text-muted-foreground">Awaiting admin approval.</span>
            {isAdmin && (
              <Button size="sm" variant="secondary" onClick={onApprove} disabled={isApproving}>
                {isApproving ? 'Approving…' : 'Approve'}
              </Button>
            )}
          </>
        )}
        {status === 'approved' && (
          <>
            <span className="text-xs text-muted-foreground">
              {row.approvedAt
                ? `Approved ${new Date(row.approvedAt).toLocaleDateString()}${
                    row.approvedCapexUsd != null ? ` · capex ${formatUsd(row.approvedCapexUsd)}` : ''
                  }`
                : 'Approved.'}
            </span>
            {isAdmin && (
              <Button size="sm" variant="secondary" onClick={onUnapprove} disabled={isUnapproving}>
                {isUnapproving ? 'Withdrawing…' : 'Withdraw approval'}
              </Button>
            )}
          </>
        )}
      </div>

      {status === 'approved' && (
        // One Voice Rule — a static status banner doesn't earn the brand
        // emerald fill/border (that's reserved for actions, active state,
        // links, focus rings); the checkmark still takes the series
        // scale's positive step for direction, same as the rest of the page.
        <div className="flex items-start gap-2 rounded-md border border-border bg-muted px-2.5 py-2 text-xs text-foreground">
          <Check className="mt-0.5 size-3.5 shrink-0" style={{ color: SX_PALETTE[3] }} />
          <span>This scenario is approved and locked. Editing any field will void the approval.</span>
        </div>
      )}

      {voidedBanner && (
        <div className="flex items-start justify-between gap-2 rounded-md border border-amber-200 bg-amber-50 px-2.5 py-2 text-xs text-amber-800">
          <span className="flex items-start gap-2">
            <AlertTriangle className="mt-0.5 size-3.5 shrink-0 text-amber-600" />
            Your edit voided this scenario's approval (approved{' '}
            {new Date(voidedBanner.approvedAt).toLocaleDateString()}). Submit it again once you're
            ready for another review.
          </span>
          <button type="button" onClick={onDismissVoided} className="shrink-0 underline">
            Dismiss
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * "Before you commit" — readiness + approval status (SIL-568). Named binary
 * rows only, no composite grade (DESIGN.md). "Approval" is a single row
 * naming the current binary state, never a score.
 */
function ReadinessChecklist({
  draft,
  scenarioId,
  isDirty,
  canAutosave,
  scenarioRow,
}: {
  draft: ScenarioDraft;
  scenarioId: string | null;
  isDirty: boolean;
  canAutosave: boolean;
  scenarioRow: FinancialScenarioRow | undefined;
}) {
  const refreshCohorts = draft.cohorts.filter((c) => c.action === 'refresh');
  const items: { title: string; sub: ReactNode; ok: boolean }[] = [
    {
      title: 'Scenario saved',
      sub: isDirty ? 'You have unsaved changes.' : scenarioId ? 'All changes saved.' : 'Not saved yet.',
      ok: !!scenarioId && !isDirty,
    },
    {
      title: 'No stranded cohorts',
      sub: canAutosave
        ? 'Every cohort acts within the horizon.'
        : 'One or more cohorts act past the horizon — fix on Define paths.',
      ok: canAutosave,
    },
  ];
  if (refreshCohorts.length > 0) {
    const allPicked = refreshCohorts.every((c) => c.refreshSkuId);
    items.push({
      title: 'Replacement SKUs selected',
      sub: allPicked ? (
        'Every refresh cohort has a target SKU.'
      ) : scenarioId ? (
        <>
          Pick a replacement SKU on the{' '}
          <Link
            to="/financials/scenarios/$scenarioId"
            params={{ scenarioId }}
            search={{ stage: 1, tab: 'replacements' }}
            className="underline"
          >
            Replacements tab
          </Link>
          .
        </>
      ) : (
        'Pick a replacement SKU on the Replacements tab.'
      ),
      ok: allPicked,
    });
  }

  // SIL-568 — a single named binary row, never a score. `scenarioRow` is the
  // live server row (not `draft`, since status isn't part of the editable
  // draft) — absent entirely for a not-yet-saved scenario.
  if (scenarioRow) {
    const status = scenarioRow.status;
    items.push({
      title: 'Approval',
      sub:
        status === 'approved'
          ? 'Approved.'
          : status === 'in_review'
            ? 'Submitted — awaiting admin approval.'
            : 'Not yet requested.',
      ok: status === 'approved',
    });
  }

  return (
    <div className="rounded-xl border border-border bg-card p-5">
      <SectionLabel className="mb-1">Before you commit</SectionLabel>
      <p className="mb-3 text-xs text-muted-foreground">Named checks only — never a composite readiness score.</p>
      <div className="space-y-2 text-sm">
        {items.map((it) => (
          <div key={it.title} className="flex items-start gap-2">
            {it.ok ? (
              // One Voice Rule — a static pass/fail glyph doesn't get the
              // brand emerald token; use the series scale's positive step
              // like every other directional figure on this page.
              <Check className="mt-0.5 size-4 shrink-0" style={{ color: SX_PALETTE[3] }} />
            ) : (
              <AlertTriangle className="mt-0.5 size-4 shrink-0 text-amber-600" />
            )}
            <span>
              <span className="font-medium text-foreground">{it.title}</span>
              <span className="ml-2 text-muted-foreground">{it.sub}</span>
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sensitivity — swing on net (SIL-306, W4, spec slice 4) — one-at-a-time
// tornado bars over `GET /scenarios/:id/sensitivity` (SIL-299). An
// `applicable: false` attribute renders GREYED with its reason and NO bar —
// a zero-length bar would read as "tested and doesn't matter", the opposite
// of "couldn't test it" (spec: "must never read as insensitive"). Shares
// `useScenarioSensitivityQuery`'s cache key with `AssumptionTable` — same
// rows, same order, one fetch.
// ---------------------------------------------------------------------------

export function SensitivityPanel({ scenarioId }: { scenarioId: string | null }) {
  const q = useScenarioSensitivityQuery(scenarioId);

  // The permission/existence gate: no scenarioId means there is nothing to
  // fetch, which is a precondition, not an error state — QueryBoundary
  // below covers loading/error/empty for every case that DOES have a query.
  if (!scenarioId) return null;

  return (
    <QueryBoundary
      query={q}
      errorTitle="Couldn't load sensitivity."
      // A failed or absent fetch used to `return null` here — silently
      // indistinguishable from "the assumptions don't swing the answer".
      // The reader concluded the model was tested when nothing ran; that
      // was the single most dangerous line on this page. It now surfaces
      // the shared ErrorState with retry instead.
      empty={(data) => data.attributes.length === 0}
      emptyState={
        <div className="rounded-xl border border-border bg-card p-5 text-xs text-muted-foreground">
          No sensitivity attributes apply to this scenario yet.
        </div>
      }
      loading={<LoadingShell rows={5} />}
    >
      {(data) => {
        const attrs = data.attributes;
        const maxSwing = Math.max(
          1,
          ...attrs
            .filter((a) => a.applicable && a.lowNetUsd !== null && a.highNetUsd !== null)
            .map((a) => Math.max(Math.abs(a.lowNetUsd! - a.baseNetUsd), Math.abs(a.highNetUsd! - a.baseNetUsd))),
        );

        return (
          <div className="rounded-xl border border-border bg-card p-5">
            <SectionLabel className="mb-3">Sensitivity — swing on net</SectionLabel>
            <div className="space-y-3">
              {attrs.map((a) => (
                <div key={a.id} className="flex items-center gap-3">
                  <div className="w-36 shrink-0 text-xs font-medium text-foreground">
                    <span className="inline-flex items-center gap-1">
                      {a.label}
                      {a.applicable && <CalculationBasisTrigger title={a.label} basis={a.basis} />}
                    </span>
                  </div>
                  {a.applicable && a.lowNetUsd !== null && a.highNetUsd !== null ? (
                    <TornadoBar low={a.lowNetUsd} high={a.highNetUsd} base={a.baseNetUsd} maxSwing={maxSwing} />
                  ) : (
                    <div className="flex-1 text-xs italic text-muted-foreground">
                      Not applicable — {a.reason ?? 'this cohort has no matching data'}
                    </div>
                  )}
                  {a.applicable && a.swingUsd !== null && (
                    <div className="w-24 shrink-0 text-right font-num text-xs text-muted-foreground">
                      ±{formatUsd(Math.abs(a.swingUsd), { compact: true })}
                    </div>
                  )}
                  {/* Carbon column — same row, always rendered (SIL-349): a
                      `pue` perturbation moves both dollars and carbon
                      together; every other attribute reports an explicit
                      zero swing here, never a blank cell. */}
                  <div className="w-36 shrink-0 text-right">
                    <SensitivityCarbonSwing attr={a} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      }}
    </QueryBoundary>
  );
}

function TornadoBar({
  low,
  high,
  base,
  maxSwing,
}: {
  low: number;
  high: number;
  base: number;
  maxSwing: number;
}) {
  const lowDelta = low - base;
  const highDelta = high - base;
  const negPct = (Math.max(0, -Math.min(lowDelta, highDelta)) / maxSwing) * 50;
  const posPct = (Math.max(0, Math.max(lowDelta, highDelta)) / maxSwing) * 50;
  return (
    <div className="relative h-4 flex-1 rounded bg-muted">
      <div className="absolute inset-y-0 left-1/2 w-px bg-border" />
      <div
        className="absolute inset-y-0 rounded-l"
        style={{ right: '50%', width: `${negPct}%`, backgroundColor: SX_PALETTE[5], opacity: 0.55 }}
      />
      <div
        className="absolute inset-y-0 rounded-r"
        style={{ left: '50%', width: `${posPct}%`, backgroundColor: SX_PALETTE[3], opacity: 0.55 }}
      />
    </div>
  );
}

function AssumptionChip({ label, children }: { label: string; children?: ReactNode }) {
  return (
    <span className="inline-flex items-center gap-1 rounded-full border border-border bg-muted px-2.5 py-1 text-xs font-medium text-foreground">
      {label}
      {children && <InfoTooltip label={`About ${label}`}>{children}</InfoTooltip>}
    </span>
  );
}

export function NetPositionHero({
  metrics,
  basis,
  generatedAt,
  sourcedCount,
  totalRows,
  sourcedRows,
}: {
  metrics: ScenarioCompareResponse['metrics'];
  basis?: ScenarioCompareResponse['basis'];
  /** Previously rendered in exactly ONE place on this page —
   * `LotTeamCohortMembership`, which `return null`s when there are no
   * lot/team cohorts. A class-scoped scenario printed a dollar delta with
   * no timestamp anywhere. PRODUCT.md principle 2 ("confidence travels
   * with the claim") puts it here instead, next to the claim itself. */
  generatedAt: string;
  sourcedCount: number;
  totalRows: number;
  sourcedRows: { label: string; sourced: boolean }[];
}) {
  const delta = metrics.delta.netPositionUsd;
  const coverage = totalRows > 0 ? sourcedCount / totalRows : 0;
  const tier = coverageTier(coverage);
  return (
    <div className="rounded-xl border border-border bg-card p-6">
      <SectionHead
        trigger={<CalculationBasisTrigger title="Net position delta vs holding" basis={basis} />}
      >
        Net economic impact — your scenario vs. holding
      </SectionHead>
      <SignedUsd value={delta} className="mt-1 text-3xl font-semibold" />
      <div className="mt-2 text-xs text-muted-foreground">
        Baseline (hold): {formatUsd(metrics.baseline.netPositionUsd)} · Scenario:{' '}
        {formatUsd(metrics.scenario.netPositionUsd)}
      </div>

      {/* Confidence + as-of — travels with the claim (PRODUCT.md principle
          2), using the coverage-ring signature: ring, number, and status
          word all take one accent driven by the tier. */}
      <div className="mt-5 flex flex-wrap items-center gap-4 border-t border-border pt-4">
        <CoverageRing score={sourcedCount} coverage={coverage} tier={tier} />
        <div className="flex min-w-0 flex-col gap-1">
          <span className="inline-flex flex-wrap items-center gap-1.5">
            <span className={cn('text-sm font-medium', TIER_ACCENT[tier])}>
              {sourcedCount} of {totalRows} assumption rows sourced
            </span>
            <InfoTooltip label="Which assumption rows are sourced">
              <p className="mb-2 font-medium text-foreground">
                {sourcedCount} of {totalRows} rows have a real, attributable source:
              </p>
              <ul className="space-y-1">
                {sourcedRows.map((r) => (
                  <li key={r.label} className="flex items-center justify-between gap-3">
                    <span>{r.label}</span>
                    <span className={r.sourced ? 'font-medium text-foreground' : 'text-muted-foreground'}>
                      {r.sourced ? 'Sourced' : 'Not sourced'}
                    </span>
                  </li>
                ))}
              </ul>
            </InfoTooltip>
          </span>
          <span className="text-xs text-muted-foreground">As of {formatTs(generatedAt)}</span>
        </div>
      </div>
    </div>
  );
}

const METRIC_ROWS: { key: keyof ScenarioCompareResponse['metrics']['baseline']; label: string }[] = [
  { key: 'recoveredValueUsd', label: 'Recovered value' },
  { key: 'residualFmvUsd', label: 'Residual FMV' },
  { key: 'powerCostUsd', label: 'Power cost' },
  { key: 'refreshCapexUsd', label: 'Capex (refresh + buy)' },
  { key: 'netPositionUsd', label: 'Net position' },
];

/**
 * Epic 2 (plans/lots-first-class.md) — member count + "as of" disclosure for
 * every lot/team-scoped cohort. Resolution is live (matches the existing
 * `assetClassId` posture) and lot/team MEMBERSHIP carries no history (Epic 1
 * "no history"), so the same scenario can resolve to a different number of
 * assets — and a different dollar figure — between two opens with zero
 * edits. Rendered ONLY for lot/team cohorts: an `all`/`class` cohort's
 * membership is exactly as stable as it has always been, so calling out
 * "live" there would be noise, not disclosure.
 *
 * SIL — scenario freeze-on-approval follow-up: `compareResult.byCohort`
 * (and the "as of" timestamp with it) is frozen too when the caller is
 * citing an approval snapshot — passing `frozen` swaps the tooltip copy so
 * it doesn't tell the reader "this drifts between visits" about a count
 * that, for this specific citation, provably cannot.
 */
export function LotTeamCohortMembership({
  draft,
  compareResult,
  frozen = false,
}: {
  draft: ScenarioDraft;
  compareResult: ScenarioCompareResponse;
  frozen?: boolean;
}) {
  const rows = draft.cohorts
    .filter((c) => c.scope === 'lot' || c.scope === 'team')
    .map((c) => ({
      key: c.key,
      label: c.label,
      scope: c.scope,
      assetCount: compareResult.byCohort?.find((bc) => bc.cohortId === c.id)?.assetCount ?? null,
    }));

  if (rows.length === 0) return null;

  return (
    <div className="rounded-xl border border-border bg-card p-5">
      <SectionHead
        trigger={
          <InfoTooltip label="About lot/team cohort membership">
            {frozen
              ? "This count is frozen from the approved snapshot — it won't change between visits, even though lot/team membership itself isn't tracked historically."
              : "Lot and team membership isn't tracked historically — a cohort's asset list is resolved fresh every time this scenario is computed, so the count below (and the dollars it feeds) can change between visits even with no edits to the scenario itself."}
          </InfoTooltip>
        }
      >
        Lot &amp; team cohort membership
      </SectionHead>
      <div className="mt-2 space-y-1.5">
        {rows.map((r) => (
          <div key={r.key} className="flex items-center justify-between gap-3 text-xs">
            <span className="text-foreground">
              {r.label} <span className="text-muted-foreground">({r.scope === 'lot' ? 'lot' : 'team'})</span>
            </span>
            <span className="font-num text-muted-foreground" data-testid={`cohort-membership-${r.key}`}>
              {r.assetCount === null ? '—' : `${r.assetCount} asset${r.assetCount === 1 ? '' : 's'}`} ·{' '}
              {frozen ? 'frozen as of' : 'as of'} {formatTs(compareResult.generatedAt)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function MetricTable({ metrics }: { metrics: ScenarioCompareResponse['metrics'] }) {
  return (
    <div className="overflow-hidden rounded-md border border-border print:break-inside-avoid">
      {/* Ground-filled header at the standard 0.75rem floor (DESIGN.md:
          "nothing in the app renders text below either floor") — this row
          was 9.5px, below the floor, on the money table. */}
      <div
        className="grid bg-muted px-3 py-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground"
        style={{ gridTemplateColumns: 'minmax(0,1.4fr) repeat(3, minmax(0,1fr))' }}
      >
        <span>Metric</span>
        <span className="text-right">Baseline</span>
        <span className="text-right">Scenario</span>
        <span className="text-right">Delta</span>
      </div>
      {METRIC_ROWS.map((row) => {
        const delta = metrics.delta[row.key];
        return (
          <div
            key={row.key}
            className="grid border-t border-border px-3 py-1.5 text-[13px] text-foreground"
            style={{ gridTemplateColumns: 'minmax(0,1.4fr) repeat(3, minmax(0,1fr))' }}
          >
            <span className="text-muted-foreground">{row.label}</span>
            <span className="font-num text-right">{formatUsd(metrics.baseline[row.key])}</span>
            <span className="font-num text-right">{formatUsd(metrics.scenario[row.key])}</span>
            <SignedUsd value={delta} className="text-right" />
          </div>
        );
      })}
    </div>
  );
}
